/**
 * 📋 BOOKINGBASICINFO - Компонент основной информации о бронировании
 * Этапы 1-8: Полностью финализированная версия для Риксос
 * 
 * @module BookingBasicInfo
 * @description Форма редактирования основной информации бронирования:
 * - 👤 Данные гостя (ФИО, телефон, email) с приоритетом из Selectel
 * - 📅 Даты заезда/выезда с проверкой доступности номера
 * - 🏨 Выбор категории номера и конкретного номера (показывает все номера с пометкой занят/доступен)
 * - 💰 Тарифный план с автоматическим расчетом цены
 * - 👥 Количество гостей (взрослые, дети, младенцы, кроватки)
 * - 💸 Скидки (процентные и фиксированные)
 * - ✨ VIP статус и дополнительные услуги (трансфер, диета)
 * - 💾 Сохранение ТОЛЬКО при нажатии кнопки "Сохранить изменения" (БЕЗ автосохранения)
 * - ✅ Валидация всех полей с визуальной индикацией ошибок
 * 
 * @features
 * - ⚡ Real-Time синхронизация через Supabase
 * - 🔄 Автоматический пересчет финансов при изменении параметров (локально)
 * - 📝 Автоматическое логирование всех изменений в booking_history
 * - 🔒 Синхронизация персональных данных с Selectel (152-ФЗ)
 * - 🎨 Визуальные индикаторы статуса сохранения
 * - 🏨 Умный выбор номеров: показывает ВСЕ номера категории с пометками доступности
 * 
 * @author Lovable AI
 * @since 2025-11-10
 * @version 8.0
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRoomAvailability, conflictText } from '@/hooks/useRoomAvailability';
import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as UICalendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { useRooms, Room } from '@/hooks/useRooms';
import { useRatePlans } from '@/hooks/useRatePlans';
import { useRoomCategories } from '@/hooks/useRoomCategories';
import { InvoiceGenerator } from './InvoiceGenerator';
import { GuestLinkedRooms } from './GuestLinkedRooms';
// AddRoomModal теперь в SuperAdvancedModal
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Crown, CreditCard, Calendar, DoorOpen, Percent, Sparkles, Lock, Unlock, User, Phone, Mail, Users, Car, UtensilsCrossed, MessageSquare, CalendarIcon, AlertCircle, CheckCircle2, Check, Save, Trash2, ChevronDown, ChevronRight, Loader2, Plus, Building, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { format, differenceInDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { validateBookingForm } from '@/utils/bookingValidation';
import { cn } from '@/lib/utils';
// logMultipleChangesWithContext удалён — логирование делается атомарно в RPC save_booking_atomic
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';

/**
 * Пропсы для компонента BookingBasicInfo
 * 
 * @interface BookingBasicInfoProps
 * @property {BookingWithFinancials} booking - Полные данные бронирования
 * @property {function} onUpdate - Коллбэк для обновления данных бронирования
 *   - Может быть синхронным (void) или асинхронным (Promise<void>)
 */
interface BookingBasicInfoProps {
  booking: BookingWithFinancials;
  onUpdate: (updates: Partial<BookingWithFinancials>) => void | Promise<void>;
  onClose?: () => void; // Закрытие модала после успешного сохранения
  rooms?: Room[]; // ⚡ АК-47: Передаём rooms из кэша для избежания дублирующего запроса
  onUnsavedChange?: (hasUnsaved: boolean) => void; // Сигнал модалу о несохранённых данных
  onSaveRef?: (saveFn: (() => Promise<void>) | null) => void; // Передача save-функции наверх
  onTabChange?: (tab: string) => void; // Переход на другую вкладку модала
  onBookingSelect?: (bookingId: string) => void; // Навигация к связанной брони
}

/**
 * BookingBasicInfo - Форма редактирования основной информации бронирования
 * 
 * @component
 * @param {BookingBasicInfoProps} props - Пропсы компонента
 * @returns {React.FC<BookingBasicInfoProps>}
 * 
 * @description
 * Основной компонент для редактирования всех базовых данных бронирования:
 * 
 * **Разделы формы:**
 * 1. Информация о госте (имя, фамилия, отчество, телефон, email)
 * 2. Даты и время (заезд, выезд с проверкой доступности)
 * 3. Номер (категория и конкретный номер с визуализацией)
 * 4. Тариф (выбор тарифного плана)
 * 5. Гости (взрослые, дети, младенцы, кроватки)
 * 6. Скидки (процентные или фиксированные)
 * 7. VIP услуги (статус, трансфер, диета)
 * 8. Особые пожелания (текстовое поле)
 * 
 * **Автоматизация:**
 * - Локальный пересчет финансов при изменении параметров
 * - Валидация данных с визуальными индикаторами ошибок
 * - Сохранение только при нажатии кнопки "Сохранить изменения"
 * - Автоматический пересчет total_amount при изменении дат, номера, гостей, скидок
 * - Автоматическая проверка конфликтов номеров при смене дат
 * - Автоматическое логирование изменений в booking_history (только при ручном сохранении)
 * 
 * **Валидация:**
 * - Обязательные поля: имя гостя, даты, номер
 * - Форматы: телефон, email
 * - Логические проверки: дата выезда > дата заезда, количество гостей > 0
 * - Визуальная индикация ошибок (красные границы, иконки, подсказки)
 * 
 * **Интеграции:**
 * - Selectel MySQL: синхронизация персональных данных гостей (152-ФЗ)
 * - Supabase: основные данные бронирования
 * - React Query: кеширование категорий, номеров, тарифов
 * 
 * @remarks
 * - ВАЖНО: Персональные данные (имя, телефон, email) хранятся в Selectel MySQL
 * - Технические данные (даты, цены) хранятся в Supabase
 * - Автосохранение НЕ выполняет полную валидацию (только ручное сохранение)
 * - При конфликте номера автоматически предлагаются альтернативные варианты
 * 
 * @example
 * ```tsx
 * <BookingBasicInfo
 *   booking={currentBooking}
 *   onUpdate={async (updates) => {
 *     await updateBooking(updates);
 *     await refetchBookings();
 *   }}
 * />
 * ```
 */
export const BookingBasicInfo: React.FC<BookingBasicInfoProps> = ({
  booking, 
  onUpdate,
  onClose,
  rooms: externalRooms, // ⚡ АК-47: Получаем rooms из родителя (из кэша)
  onUnsavedChange,
  onSaveRef,
  onTabChange,
  onBookingSelect
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentHotel } = useOptimizedHotel();
  const { toast } = useToast();
  
  // 🔥 AK-47: Broadcast для синхронизации времени заезда/выезда между компьютерами
  const { broadcastFinancialChange } = useRealtime();
  
  // ⚡ АК-47: Используем внешние rooms если переданы, иначе фетчим (fallback)
  const { rooms: fetchedRooms, loading: fetchedRoomsLoading } = useRooms();
  const rooms = externalRooms || fetchedRooms;
  const roomsLoading = externalRooms ? false : fetchedRoomsLoading;
  
  const { ratePlans, loading: ratePlansLoading } = useRatePlans();
  const { categories: dbCategories, loading: categoriesLoading } = useRoomCategories();
  
  // Преобразуем категории в формат, совместимый с кодом
  const categories = useMemo(() => 
    dbCategories.map(cat => ({
      id: cat.id,
      name: cat.name,
      description: cat.description
    })),
    [dbCategories]
  );

  // 🎯 Этап 2: Состояние для диалога смены категории
  const [roomTypeChangeDialog, setRoomTypeChangeDialog] = useState<{
    show: boolean;
    newRoomType?: string;
    categoryId?: string;
  }>({ show: false });

  // Состояния формы - ПРИОРИТЕТ данным из Selectel (Риксос стандарт)
  const [formData, setFormData] = useState<any>(() => {
    // 🛡️ Helper: проверяет что значение НЕ placeholder
    const isPlaceholder = (val: string | undefined): boolean => {
      if (!val) return true;
      const trimmed = val.trim();
      if (trimmed.length === 0) return true;
      const placeholders = [
        'загрузка...', 'loading...', 'загрузка', 'loading',
        'гость', 'guest', 'гость (данные не найдены)', 
        'гость (ошибка загрузки)', 'undefined', 'null',
        'unknown', 'unknown guest',
        'block-converted@placeholder.local'
      ];
      return placeholders.includes(trimmed.toLowerCase());
    };
    
    // 🎯 Берем данные из Selectel если есть и НЕ placeholder, иначе из booking_core
    const rawSelectelFirstName = booking.selectel_guest_data?.first_name;
    const rawSelectelLastName = booking.selectel_guest_data?.last_name;
    const rawSelectelMiddleName = booking.selectel_guest_data?.middle_name;
    
    const selectelFirstName = isPlaceholder(rawSelectelFirstName) ? '' : rawSelectelFirstName || '';
    const selectelLastName = isPlaceholder(rawSelectelLastName) ? '' : rawSelectelLastName || '';
    const selectelMiddleName = isPlaceholder(rawSelectelMiddleName) ? '' : rawSelectelMiddleName || '';
    const selectelName = booking.selectel_guest_data?.full_name || booking.guest_name || '';
    const selectelPhone = booking.selectel_guest_data?.phone || booking.guest_phone || '';
    const rawEmail = booking.selectel_guest_data?.email || booking.guest_email || '';
    const selectelEmail = isPlaceholder(rawEmail) ? '' : rawEmail;
    
    // 🔥 Используем структурированные данные из Selectel или разбиваем full_name
    let firstName = selectelFirstName;
    let lastName = selectelLastName;
    let middleName = selectelMiddleName;
    
    if (!firstName && selectelName && !isPlaceholder(selectelName)) {
      // Убираем " Guest" если есть
      const cleanName = selectelName.replace(' Guest', '').replace('Гость ', '').trim();
      
      // 🎯 Разбиваем по пробелу в порядке: Фамилия Имя Отчество (русский стандарт)
      const nameParts = cleanName.split(' ').filter(p => p.length > 0);
      if (nameParts.length >= 3) {
        lastName = nameParts[0];    // Первое слово = Фамилия
        firstName = nameParts[1];   // Второе слово = Имя
        middleName = nameParts.slice(2).join(' '); // Остальное = Отчество
      } else if (nameParts.length === 2) {
        lastName = nameParts[0];    // Фамилия
        firstName = nameParts[1];   // Имя
      } else if (nameParts.length === 1) {
        lastName = nameParts[0];    // Только фамилия
      }
    }
    
    // 🔥 КРИТИЧНО: Если room_category_id отсутствует, находим его по room_id
    let roomCategoryId = (booking as any).room_category_id || '';
    
    // Если room_id есть, но category_id нет - находим категорию через номер
    if (booking.room_id && !roomCategoryId && rooms.length > 0) {
      const foundRoom = rooms.find(r => r.id === booking.room_id);
      if (foundRoom) {
        roomCategoryId = foundRoom.room_category_id;
        console.log('✅ Восстановлен room_category_id из номера:', roomCategoryId);
      }
    }
    
    const initialData = {
      guest_first_name: firstName,
      guest_last_name: lastName,
      guest_middle_name: middleName,
      guest_name: selectelName,
      guest_phone: selectelPhone,
      guest_email: selectelEmail,
      check_in_date: booking.check_in_date,
      check_in_time: (booking as any).check_in_time || '15:00',
      check_out_date: booking.check_out_date,
      check_out_time: (booking as any).check_out_time || '12:00',
      room_type: booking.room_type || '',
      room_category_id: roomCategoryId,
      room_id: booking.room_id || null,
      rate_plan_id: (booking as any).rate_plan_id || null,
      adult_count: (booking as any).adult_count ?? 1,
      children_count: (booking as any).children_count ?? 0,
      infants_count: (booking as any).infants_count ?? 0,
      cribs_count: (booking as any).cribs_count ?? 0,
      extra_beds: (booking as any).extra_beds ?? 0, // 🎯 AK-47 v3: доп. место
      discount_percentage: (booking as any).discount_percentage || 0,
      discount_amount: (booking as any).discount_amount || 0,
      base_price: (booking as any).base_price || 0,
      total_amount: booking.total_amount,
      // 🎯 AK-47 v3: серверная разбивка из booking_financial (источник истины)
      accommodation_amount: Number(booking.financial?.accommodation_amount ?? booking.total_amount) || 0,
      extra_guests_charge: Number(
        booking.financial?.extra_guests_charge ??
        (booking as any).extra_guests_charge ??
        (Array.isArray((booking as any).financial) ? (booking as any).financial[0]?.extra_guests_charge : 0) ??
        0
      ) || 0,
      manual_total_amount: (booking as any).manual_total_amount || 0,
      price_locked: (booking as any).price_locked || false,
      vip_status: (booking as any).vip_status || false,
      transfer_needed: (booking as any).transfer_needed || false,
      dietary_restrictions: (booking as any).dietary_restrictions || '',
      special_requests: booking.special_requests || '',
      no_relocation: (booking as any).no_relocation || false,
      source: (booking as any).source || 'manual', // 🎯 Источник бронирования
    };
    
    console.log('📋 Инициализация formData (существующее бронирование):', {
      bookingId: booking.id,
      room_type: initialData.room_type,
      room_category_id: initialData.room_category_id,
      room_id: initialData.room_id,
      guest_name: initialData.guest_name,
      dataSource: roomCategoryId ? 'restored from room' : 'from booking'
    });
    
    return initialData;
  });

  // 🎛️ Состояния
  const [isValidating, setIsValidating] = useState(false);
  const [validationPassed, setValidationPassed] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isPriceLoading, setIsPriceLoading] = useState(true); // 💰 Состояние загрузки цены из room_rates
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationComment, setCancellationComment] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // 🔄 Восстановление брони
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreCategoryId, setRestoreCategoryId] = useState('');
  const [restoreRoomId, setRestoreRoomId] = useState('');
  
  // 🚨 Защита от раннего заселения
  const [showEarlyCheckinDialog, setShowEarlyCheckinDialog] = useState(false);
  const [earlyCheckinDays, setEarlyCheckinDays] = useState(0);
  const [pendingCheckinConfirmed, setPendingCheckinConfirmed] = useState(false);
  // Занятость номера определяет сервер (см. useRoomAvailability ниже)

  // Состояние для сохранения (БЕЗ автосохранения по требованию пользователя)
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(true);
  
  // Состояния для управления popover календарей
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  
  // 📊 Состояния для тарификации по дням
  const [dailyRates, setDailyRates] = useState<{ date: string; price: number; seasonName?: string; ratePlanName?: string; }[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [showDailyRates, setShowDailyRates] = useState(false);
  
  // 🏨 Модал добавления номера теперь в SuperAdvancedModal

  // 🚀 useRooms автоматически загружает номера при монтировании
  
  // 🔥 КРИТИЧНО: Синхронизация room_category_id при загрузке номеров для существующих бронирований
  useEffect(() => {
    if (!formData.room_category_id && formData.room_id && rooms.length > 0) {
      const foundRoom = rooms.find(r => r.id === formData.room_id);
      if (foundRoom && foundRoom.room_category_id) {
        console.log('🔄 Синхронизация room_category_id для существующего бронирования:', {
          room_id: formData.room_id,
          room_category_id: foundRoom.room_category_id,
          room_type: foundRoom.room_type
        });
        setFormData((prev: any) => ({
          ...prev,
          room_category_id: foundRoom.room_category_id,
          room_type: foundRoom.room_type || prev.room_type
        }));
      }
    }
  }, [rooms.length, formData.room_id, formData.room_category_id]);

  // 🔥 AK-47: Синхронизация formData с booking prop при изменении version + данных гостя + критичных полей
  // Это гарантирует что форма отражает актуальные данные если booking обновился извне
  useEffect(() => {
    const bookingVersion = (booking as any).version || 0;
    const formVersion = (formData as any)._version || 0;
    
    // Проверяем есть ли новые данные Selectel которых нет в форме
    const selectelData = (booking as any).selectel_guest_data;
    const hasNewSelectelData = selectelData && 
      !formData.guest_name && 
      (selectelData.full_name || selectelData.first_name);
    
    // 🔥 КРИТИЧНО: Проверяем рассинхронизацию критичных полей с БД
    const bookingPriceLocked = (booking as any).price_locked ?? false;
    const bookingManualAmount = (booking as any).manual_total_amount ?? null;
    const bookingVipStatus = (booking as any).vip_status ?? false;
    const bookingTransferNeeded = (booking as any).transfer_needed ?? false;
    const bookingNoRelocation = (booking as any).no_relocation ?? false;
    
    const hasCriticalFieldsMismatch = 
      formData.price_locked !== bookingPriceLocked ||
      formData.manual_total_amount !== bookingManualAmount ||
      formData.vip_status !== bookingVipStatus ||
      formData.transfer_needed !== bookingTransferNeeded ||
      formData.no_relocation !== bookingNoRelocation;
    
    if (bookingVersion > formVersion || hasNewSelectelData || hasCriticalFieldsMismatch) {
      console.log('⚡ BookingBasicInfo: Syncing formData with updated booking', { 
        bookingVersion, 
        formVersion, 
        hasNewSelectelData,
        hasCriticalFieldsMismatch,
        bookingPriceLocked,
        formPriceLocked: formData.price_locked,
        selectelData 
      });
      
      // 🛡️ Helper: проверяем что значение НЕ placeholder (повторно для синхронизации)
      const isSyncPlaceholder = (val: string | undefined): boolean => {
        if (!val) return true;
        const trimmed = val.trim();
        if (trimmed.length === 0) return true;
      const placeholders = [
          'загрузка...', 'loading...', 'загрузка', 'loading',
          'гость', 'guest', 'гость (данные не найдены)', 
          'гость (ошибка загрузки)', 'undefined', 'null',
          'unknown', 'unknown guest',
          'block-converted@placeholder.local'
        ];
        return placeholders.includes(trimmed.toLowerCase());
      };
      
      // Берём данные из Selectel если есть И они НЕ placeholder
      const rawSelectelFirstName = selectelData?.first_name;
      const rawSelectelLastName = selectelData?.last_name;
      const rawSelectelMiddleName = selectelData?.middle_name;
      
      const selectelFirstName = isSyncPlaceholder(rawSelectelFirstName) ? '' : rawSelectelFirstName || '';
      const selectelLastName = isSyncPlaceholder(rawSelectelLastName) ? '' : rawSelectelLastName || '';
      const selectelMiddleName = isSyncPlaceholder(rawSelectelMiddleName) ? '' : rawSelectelMiddleName || '';
      const selectelName = selectelData?.full_name || (booking as any).guest_name || '';
      const selectelPhone = selectelData?.phone || (booking as any).guest_phone || '';
      const rawSyncEmail = selectelData?.email || (booking as any).guest_email || '';
      const selectelEmail = isSyncPlaceholder(rawSyncEmail) ? '' : rawSyncEmail;
      
      // Разбиваем имя если нет структурированных данных И full_name не placeholder
      let firstName = selectelFirstName;
      let lastName = selectelLastName;
      let middleName = selectelMiddleName;
      
      if (!firstName && selectelName && !isSyncPlaceholder(selectelName)) {
        const cleanName = selectelName.replace(' Guest', '').replace('Гость ', '').trim();
        const nameParts = cleanName.split(' ').filter((p: string) => p.length > 0);
        if (nameParts.length >= 3) {
          lastName = nameParts[0];    // Фамилия
          firstName = nameParts[1];   // Имя
          middleName = nameParts.slice(2).join(' '); // Отчество
        } else if (nameParts.length === 2) {
          lastName = nameParts[0];    // Фамилия
          firstName = nameParts[1];   // Имя
        } else if (nameParts.length === 1) {
          lastName = nameParts[0];    // Только фамилия
        }
      }
      
      // 🛡️ АК-47: после сохранения НЕ перезаписываем гостевые поля (данные ещё не загружены из Selectel)
      if (justSavedRef.current) {
        justSavedRef.current = false;
        setFormData((prev: any) => ({
          ...prev,
          total_amount: booking.total_amount,
          // 🛏️ AK-47 v3: подтягиваем серверную разбивку чтобы UI показал «Доп. место»
          accommodation_amount: Number(
            booking.financial?.accommodation_amount ??
            (Array.isArray((booking as any).financial) ? (booking as any).financial[0]?.accommodation_amount : undefined) ??
            (booking as any).accommodation_amount ??
            booking.total_amount
          ) || 0,
          extra_guests_charge: Number(
            booking.financial?.extra_guests_charge ??
            (Array.isArray((booking as any).financial) ? (booking as any).financial[0]?.extra_guests_charge : undefined) ??
            (booking as any).extra_guests_charge ??
            0
          ) || 0,
          extra_beds: (booking as any).extra_beds ?? prev.extra_beds ?? 0,
          price_locked: bookingPriceLocked,
          check_in_date: booking.check_in_date,
          check_out_date: booking.check_out_date,
          room_id: booking.room_id,
          _version: bookingVersion
        }));
        return;
      }

      setFormData((prev: any) => ({
        ...prev,
        // ✅ КРИТИЧНЫЕ ПОЛЯ ИЗ БД (должны всегда отражать реальное состояние)
        total_amount: booking.total_amount,
        // 🛏️ AK-47 v3: серверная разбивка из booking_financial
        accommodation_amount: Number(booking.financial?.accommodation_amount ?? booking.total_amount) || 0,
        extra_guests_charge: Number(
          booking.financial?.extra_guests_charge ??
          (booking as any).extra_guests_charge ??
          (Array.isArray((booking as any).financial) ? (booking as any).financial[0]?.extra_guests_charge : 0) ??
          0
        ) || 0,
        price_locked: bookingPriceLocked,
        manual_total_amount: bookingManualAmount,
        vip_status: bookingVipStatus,
        transfer_needed: bookingTransferNeeded,
        no_relocation: bookingNoRelocation,
        discount_amount: (booking as any).discount_amount ?? prev.discount_amount,
        discount_percentage: (booking as any).discount_percentage ?? prev.discount_percentage,
        base_price: (booking as any).base_price ?? prev.base_price,
        dietary_restrictions: (booking as any).dietary_restrictions ?? prev.dietary_restrictions,
        // Даты и номер
        check_in_date: booking.check_in_date,
        check_out_date: booking.check_out_date,
        room_id: booking.room_id,
        // Данные гостя из Selectel
        guest_first_name: firstName || prev.guest_first_name,
        guest_last_name: lastName || prev.guest_last_name,
        guest_middle_name: middleName || prev.guest_middle_name,
        guest_name: selectelName || prev.guest_name,
        guest_phone: selectelPhone || prev.guest_phone,
        guest_email: selectelEmail || prev.guest_email,
        _version: bookingVersion
      }));
    }
  }, [
    (booking as any).version, 
    booking.total_amount, 
    booking.check_in_date, 
    booking.check_out_date, 
    booking.room_id,
    booking.financial?.extra_guests_charge,
    booking.financial?.accommodation_amount,
    (booking as any).price_locked,
    (booking as any).manual_total_amount,
    (booking as any).vip_status,
    (booking as any).transfer_needed,
    (booking as any).no_relocation,
    (booking as any).discount_amount,
    (booking as any).discount_percentage,
    (booking as any).base_price,
    (booking as any).dietary_restrictions,
    (booking as any).selectel_guest_data,
    (booking as any).guest_name,
    (booking as any).guest_phone,
    (booking as any).guest_email
  ]);

  // 🎯 AK-47: занятость номеров считает сервер (та же функция, что и триггер защиты от двойного заселения).
  // Никаких клиентских выгрузок booking_core и догадок по датам.

  // 📊 Загрузка дневных тарифов для таблицы тарификации
  // 🔥 FIX: Используем useRef для стабильных ссылок на массивы (предотвращает бесконечный loop)
  const justSavedRef = useRef(false);
  // 🎯 AK-47 v3: разрешаем клиентский recalc только после РЕАЛЬНОГО взаимодействия юзера.
  // На mount/refetch — НЕ перетираем серверные total_amount/accommodation_amount/extra_guests_charge.
  const hasUserInteractedRef = useRef(false);
  const roomsRef = useRef(rooms);
  const categoriesRef = useRef(categories);
  const ratePlansRef = useRef(ratePlans);
  
  // Обновляем refs при изменении данных
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);
  useEffect(() => { categoriesRef.current = categories; }, [categories]);
  useEffect(() => { ratePlansRef.current = ratePlans; }, [ratePlans]);
  
  useEffect(() => {
    // 🔥 Предотвращаем повторные вызовы пока идёт загрузка
    if (isLoadingRates) return;
    
    const loadDailyRates = async () => {
      if (!currentHotel?.id || !formData.check_in_date || !formData.check_out_date) {
        setDailyRates([]);
        return;
      }

      setIsLoadingRates(true);
      try {
        // Получаем category_id (используем refs для стабильности)
        let categoryId = formData.room_category_id || null;
        
        if (!categoryId && formData.room_id && roomsRef.current.length > 0) {
          const foundRoom = roomsRef.current.find(r => r.id === formData.room_id);
          categoryId = foundRoom?.room_category_id || null;
        }
        
        if (!categoryId && formData.room_type && categoriesRef.current.length > 0) {
          const foundCat = categoriesRef.current.find(c => c.name.toLowerCase().includes(formData.room_type.toLowerCase()));
          categoryId = foundCat?.id || null;
        }

        if (!categoryId) {
          setDailyRates([]);
          setIsLoadingRates(false);
          return;
        }

        // Загружаем сезоны
        const { data: seasonsData } = await supabase
          .from('seasons')
          .select('id, name, start_date, end_date')
          .eq('hotel_id', currentHotel.id);

        // Получаем имя тарифа (используем ref)
        let ratePlanName = 'Базовый тариф';
        if (formData.rate_plan_id) {
          const plan = ratePlansRef.current.find(p => p.id === formData.rate_plan_id);
          if (plan) ratePlanName = plan.name;
        }

        // AK-47: собираем список дат, затем один батч параллельных get_rate
        const startDate = new Date(formData.check_in_date);
        const endDate = new Date(formData.check_out_date);
        const dateList: string[] = [];
        for (let d = new Date(startDate); d < endDate; d.setDate(d.getDate() + 1)) {
          dateList.push(d.toISOString().split('T')[0]);
        }

        const rateResults = await Promise.all(
          dateList.map(dateStr =>
            supabase.rpc('get_rate', {
              p_hotel_id: currentHotel.id,
              p_category_id: categoryId,
              p_rate_plan_id: formData.rate_plan_id || null,
              p_date: dateStr,
            })
          )
        );

        const rates: typeof dailyRates = dateList.map((dateStr, i) => {
          const rateData = rateResults[i]?.data;
          let seasonName = 'Не сезон';
          if (seasonsData) {
            const matchingSeason = seasonsData.find(s => {
              const sStart = new Date(s.start_date);
              const sEnd = new Date(s.end_date);
              const currentDate = new Date(dateStr);
              return currentDate >= sStart && currentDate <= sEnd;
            });
            if (matchingSeason) seasonName = matchingSeason.name;
          }
          return {
            date: dateStr,
            price: rateData || 0,
            seasonName,
            ratePlanName,
          };
        });

        setDailyRates(rates);
      } catch (error) {
        console.error('Ошибка загрузки дневных тарифов:', error);
        setDailyRates([]);
      } finally {
        setIsLoadingRates(false);
      }
    };

    loadDailyRates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHotel?.id, formData.check_in_date, formData.check_out_date, formData.room_category_id, formData.room_id, formData.room_type, formData.rate_plan_id]);

  // 🎯 Показываем ВСЕ номера выбранной категории с проверкой доступности (по room_category_id)
  const categoryRooms = useMemo(() => {
    console.log('🔍 Фильтрация номеров по room_category_id:', {
      room_category_id: formData.room_category_id,
      room_type: formData.room_type,
      totalRooms: rooms.length,
      roomsData: rooms.slice(0, 3).map(r => ({ 
        id: r.id, 
        room_category_id: r.room_category_id,
        room_type: r.room_type, 
        room_number: r.room_number 
      }))
    });
    
    if (!formData.room_category_id) {
      console.log('⚠️ room_category_id не выбран');
      return [];
    }
    
    const filtered = rooms.filter(room => room.room_category_id === formData.room_category_id);
    
    console.log('✅ Номера категории', formData.room_category_id, ':', filtered.length, 'из', rooms.length, filtered.map(r => r.room_number));
    return filtered;
  }, [rooms, formData.room_category_id]);

  // 🔍 AK-47: доступность номеров — только серверный вердикт
  const {
    byRoomId: availabilityByRoom,
    isChecking: isCheckingAvailability,
    isFailed: availabilityFailed,
    isInvalidRange: availabilityInvalidRange,
  } = useRoomAvailability({
    hotelId: currentHotel?.id,
    categoryId: formData.room_category_id || null,
    checkIn: formData.check_in_date || null,
    checkOut: formData.check_out_date || null,
    checkInTime: formData.check_in_time || null,
    checkOutTime: formData.check_out_time || null,
    excludeBookingId: booking.id || null,
    enabled: Boolean(formData.room_category_id),
  });

  /** Свободен ли номер по мнению сервера. Пока идёт проверка или сервер недоступен — не утверждаем «свободен». */
  const isRoomAvailable = useCallback((room: any) => {
    if (!room) return false;
    const row = availabilityByRoom[room.id];
    return row ? row.is_available : false;
  }, [availabilityByRoom]);

  // 🎯 Номера категории, выбранной в диалоге смены категории
  const dialogRooms = useMemo(() => {
    if (!roomTypeChangeDialog.categoryId) return [];
    return rooms.filter(r => r.room_category_id === roomTypeChangeDialog.categoryId);
  }, [rooms, roomTypeChangeDialog.categoryId]);

  // 📋 Получаем выбранный номер
  const selectedRoom = useMemo(() => {
    if (!formData.room_id) return null;
    const room = rooms.find(r => r.id === formData.room_id);
    if (room) {
      console.log('✅ Выбранный номер:', room.room_number, room.name);
    } else if (formData.room_id) {
      console.warn('⚠️ Номер не найден:', formData.room_id, 'в', rooms.length, 'номерах');
    }
    return room || null;
  }, [rooms, formData.room_id]);

  // 💰 Получаем выбранный тариф (ОДИН тариф, не дублируем)
  const selectedRatePlan = useMemo(() => {
    // Если есть конкретный ID тарифа в formData - используем ТОЛЬКО его
    if (formData.rate_plan_id && formData.rate_plan_id !== '' && formData.rate_plan_id !== 'none' && formData.rate_plan_id !== 'loading') {
      const plan = ratePlans.find(rp => rp.id === formData.rate_plan_id);
      if (plan) {
        console.log('✅ Тариф выбран:', plan.name, plan.is_default ? '(по умолчанию)' : '');
        return plan;
      }
    }
    // Только если НЕТ выбранного - берем дефолтный
    const defaultPlan = ratePlans.find(rp => rp.is_default) || ratePlans[0] || null;
    if (defaultPlan) {
      console.log('📌 Тариф по умолчанию:', defaultPlan.name);
    }
    return defaultPlan;
  }, [ratePlans, formData.rate_plan_id]);

  // Устанавливаем тариф по умолчанию ОДИН РАЗ при загрузке тарифов
  useEffect(() => {
    // ТОЛЬКО если тарифы загрузились И нет выбранного тарифа
    if (ratePlans.length > 0 && !formData.rate_plan_id) {
      const defaultPlan = ratePlans.find(rp => rp.is_default) || ratePlans[0];
      if (defaultPlan) {
        console.log('🎯 Устанавливаем тариф по умолчанию ОДИН РАЗ:', defaultPlan.name, defaultPlan.id);
        setFormData(prev => ({ ...prev, rate_plan_id: defaultPlan.id }));
      }
    }
  }, [ratePlans.length]); // ТОЛЬКО когда меняется КОЛИЧЕСТВО тарифов (т.е. они загрузились)

  // АВТОСОХРАНЕНИЕ ОТКЛЮЧЕНО по требованию пользователя
  // Сохранение происходит ТОЛЬКО при нажатии кнопки "Сохранить изменения"

  // 🎯 Этап 2.2: Обработчик смены категории с проверкой доступности номеров (по room_category_id)
  const handleRoomTypeChange = async (categoryId: string) => {
    console.log('🏨 Смена категории номера (ID):', categoryId);
    
    // 🔥 КРИТИЧНО: room_type в booking_core хранит ПОЛНОЕ название категории!
    const selectedCategory = categories.find(c => c.id === categoryId);
    if (!selectedCategory) {
      console.error('❌ Категория не найдена:', categoryId);
      return;
    }

    // 🔥 NEW: Защита от обнуления цены при перемещении в виртуальную категорию (Overbooking)
    const fullCategory = dbCategories.find(c => c.id === categoryId);
    const isVirtual = (fullCategory as any)?.is_virtual === true;
    
    if (isVirtual) {
      const currentTotal = formData.total_amount || booking.total_amount || 0;
      console.log('🔒 Перемещение в виртуальную категорию — фиксируем цену:', currentTotal);
      
      handleChange({ 
        room_type: selectedCategory.name,
        room_category_id: categoryId,
        room_id: '',
        price_locked: true,
        manual_total_amount: currentTotal
      });
      
      toast({
        title: '🔒 Цена зафиксирована',
        description: `Бронирование в Overbooking: ${currentTotal.toLocaleString('ru-RU')} ₽`,
        duration: 4000
      });
      return;
    }
    
    // Обновляем room_type (ПОЛНОЕ название категории) и очищаем room_id
    // ВАЖНО: room_category_id НЕ существует в booking_core!
    handleChange({ 
      room_type: selectedCategory.name, // ✅ Полное название из category.name
      room_category_id: categoryId, // ✅ Только для внутреннего использования в formData
      room_id: '' // Очищаем при смене категории
    });

    // 1. Найти ВСЕ номера в новой категории (БЕЗ фильтра is_published!)
    // ✅ Показываем все номера со статусами доступности
    const availableRooms = rooms.filter(room => 
      room.room_category_id === categoryId
    );

    if (!availableRooms || availableRooms.length === 0) {
      const categoryName = categories.find(c => c.id === categoryId)?.name || categoryId;
      toast({
        title: '⚠️ Нет номеров',
        description: `В категории "${categoryName}" нет доступных номеров`,
        variant: 'destructive'
      });
      return;
    }

    // 2. Проверить доступность каждого номера для текущих дат
    if (!booking.id || !formData.check_in_date || !formData.check_out_date) {
      // Если нет дат, просто показываем все номера
      return;
    }

    // 3. AK-47: вердикт по доступности выносит сервер для НОВОЙ категории.
    // Здесь карта доступности ещё относится к старой категории — судить по ней нельзя.
    // Открываем диалог, статусы догрузятся из useRoomAvailability следующим рендером.
    const categoryName = categories.find(c => c.id === categoryId)?.name || '';
    setRoomTypeChangeDialog({
      show: true,
      newRoomType: categoryName,
      categoryId
    });
  };

  // 🎯 Применение выбранного номера из диалога
  const applyRoomChange = async (selectedRoom: any) => {
    // 🔥 КРИТИЧНО: room_type в booking_core хранит ПОЛНОЕ название категории!
    const category = categories.find(c => c.id === selectedRoom.room_category_id);
    if (!category) {
      console.error('❌ Категория номера не найдена:', selectedRoom.room_category_id);
      return;
    }
    
    // ВАЖНО: room_category_id НЕ сохраняется в booking_core!
    await handleChange({
      room_type: category.name, // ✅ ПОЛНОЕ название из category.name!
      room_category_id: selectedRoom.room_category_id, // ✅ Только для formData
      room_id: selectedRoom.id,
    });
    
    setRoomTypeChangeDialog({ show: false });
    
    toast({
      title: '✅ Номер изменен',
      description: `Переселен в номер ${selectedRoom.room_number}`,
    });
  };

  // ⚡ Обновление ТОЛЬКО локального состояния (БЕЗ сохранения в БД)
  const handleChange = (updates: Partial<typeof formData>) => {
    const newFormData = { ...formData, ...updates };
    setFormData(newFormData);
    setIsSaved(false);
    onUnsavedChange?.(true);
    // 🎯 AK-47 v3: помечаем что юзер реально менял поля — только после этого разрешаем клиентский recalc
    hasUserInteractedRef.current = true;
    
    // Очищаем ошибки для измененных полей
    const updatedFields = Object.keys(updates);
    const newErrors = { ...validationErrors };
    updatedFields.forEach(field => {
      delete newErrors[field];
    });
    setValidationErrors(newErrors);
  };

  // АВТОМАТИЧЕСКАЯ ПРОВЕРКА КОНФЛИКТОВ ОТКЛЮЧЕНА
  // Проверка происходит ТОЛЬКО при ручном изменении дат/номера через handleChange

  // 💎 Этап 3.1: Автоматический пересчет финансов с учетом услуг (Риксос премиум)
  useEffect(() => {
    // 🔥 КРИТИЧНО: Проверяем price_locked из ОБОИХ источников!
    // formData может быть не синхронизирован при первом рендере (race condition)
    const isPriceLocked = formData.price_locked || (booking as any).price_locked;
    
    if (isPriceLocked || !formData.check_in_date || !formData.check_out_date) {
      setIsPriceLoading(false);
      return;
    }

    // 🎯 AK-47 v3: НЕ запускаем клиентский пересчёт пока юзер ничего не менял.
    // Серверная сумма из booking_financial — единственный источник истины при открытии.
    if (!hasUserInteractedRef.current) {
      setIsPriceLoading(false);
      return;
    }

    // 💰 Показываем loader пока загружаем цены из room_rates
    setIsPriceLoading(true);

    const recalculate = async () => {
      try {
        const { recalculateBookingFinancials } = await import('@/utils/financialCalculations');

        // 🎯 КРИТИЧНО: Явно резолвим category_id как в dailyRates,
        // чтобы пересчет НЕ падал в fallback на base_price_per_night.
        let resolvedCategoryId: string | null = formData.room_category_id || null;
        if (!resolvedCategoryId && formData.room_id && rooms.length > 0) {
          const foundRoom = rooms.find(r => r.id === formData.room_id);
          resolvedCategoryId = foundRoom?.room_category_id || null;
        }
        if (!resolvedCategoryId && formData.room_type && categories.length > 0) {
          const foundCat = categories.find(c => c.name.toLowerCase().includes(formData.room_type.toLowerCase()));
          resolvedCategoryId = foundCat?.id || null;
        }
        
        // Рассчитать стоимость услуг
        const servicesAmount = booking.services?.reduce((sum, s) => sum + (s.total_price || 0), 0) || 0;
        
        // Выполнить полный пересчет
        const calculation = await recalculateBookingFinancials(
          {
            id: booking.id,
            hotel_id: currentHotel?.id || booking.hotel_id, // 🔥 КРИТИЧНО: передаём hotel_id для get_rate RPC
            category_id: resolvedCategoryId || undefined,
            check_in_date: formData.check_in_date,
            check_out_date: formData.check_out_date,
            check_in_time: formData.check_in_time,   // ⏰ Время заезда для расчёта доплат
            check_out_time: formData.check_out_time, // ⏰ Время выезда для расчёта доплат
            room_type: formData.room_type,
            room_id: formData.room_id,
            rate_plan_id: formData.rate_plan_id || null, // 🔥 null вместо undefined для UUID
            adult_count: formData.adult_count,
            children_count: formData.children_count,
            infants_count: formData.infants_count,
            discount_percentage: formData.discount_percentage,
            discount_amount: formData.discount_amount,
            price_locked: formData.price_locked,
            base_price: formData.base_price,
            manual_total_amount: formData.manual_total_amount,
          },
          servicesAmount,
          categories,
          rooms,
          ratePlans
        );
        
        // 🔥 AK-47: Обновить форму с ВСЕМИ значениями включая base_price и доплаты
        setFormData(prev => ({
          ...prev,
          base_price: calculation.avg_price_per_night, // ✅ Обновляем среднюю цену за ночь
          total_amount: calculation.total_amount,
          early_checkin_surcharge: calculation.early_checkin_surcharge,
          late_checkout_surcharge: calculation.late_checkout_surcharge,
        }));
      } catch (error) {
        console.error('❌ Ошибка пересчета финансов:', error);
      } finally {
        // 💰 Скрываем loader после загрузки цен
        setIsPriceLoading(false);
      }
    };
    
    recalculate();
  }, [
    formData.check_in_date,
    formData.check_out_date,
    formData.check_in_time,  // ⏰ Триггер при изменении времени заезда
    formData.check_out_time, // ⏰ Триггер при изменении времени выезда
    formData.discount_percentage, 
    formData.discount_amount, 
    formData.price_locked,
    formData.adult_count,
    formData.children_count,
    formData.infants_count,
    selectedRoom, 
    selectedRatePlan,
    formData.room_id, // Пересчет при изменении комнаты
    formData.room_category_id, // 🔥 КРИТИЧНО: Триггер после синхронизации category_id
    formData.rate_plan_id, // Пересчет при изменении тарифа
    booking.services?.length, // Триггер при изменении количества услуг
    categories.length,
    rooms.length,
    ratePlans.length,
    currentHotel?.id, // 🔥 Триггер пересчета при готовности hotel_id
    booking.hotel_id
  ]);

  // 💾 Сохранение с валидацией и синхронизацией Selectel (Риксос премиум)
  const handleSave = async () => {
    setIsSaving(true);
    setIsValidating(true);
    setValidationPassed(false);
    
    try {
      // Валидируем данные
      const result = await validateBookingForm(formData, booking.id);
      
      setValidationErrors(result.errors);
      
      if (!result.valid) {
        const errorMessages = Object.values(result.errors).join('; ');
        toast({
          title: "❌ Ошибка валидации",
          description: errorMessages,
          variant: "destructive",
        });
        setIsValidating(false);
        setIsSaving(false);
        return;
      }
      
      // Валидация прошла
      setValidationPassed(true);
      
      // 🔐 Синхронизируем персональные данные с Selectel (Этап 1.4)
      if (booking.guest_id) {
        try {
          const { syncGuestDataToSelectel } = await import('@/utils/selectelSync');
          
          // Добавляем timeout для Selectel запроса (5 секунд)
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Selectel timeout')), 5000)
          );
          
          const syncPromise = syncGuestDataToSelectel(booking.guest_id, {
            first_name: formData.guest_first_name,
            last_name: formData.guest_last_name,
            middle_name: formData.guest_middle_name || undefined,
            phone: formData.guest_phone,
            email: formData.guest_email,
          });

          const syncResult = await Promise.race([syncPromise, timeoutPromise]) as any;

          if (syncResult && !syncResult.success) {
            console.warn('⚠️ Selectel sync warning:', syncResult.error);
            // НЕ показываем toast - продолжаем молча
          } else {
            // 🎯 AK-47: Очищаем локальный Selectel кэш + инвалидируем React Query
            console.log('📡 Selectel synced, clearing cache and refreshing grid');
            window.dispatchEvent(new CustomEvent('clear-selectel-cache'));
            justSavedRef.current = true;
            await queryClient.invalidateQueries({ queryKey: ['optimized-bookings'] });
            await queryClient.invalidateQueries({ queryKey: ['bookings'] });
            await queryClient.invalidateQueries({ queryKey: ['grid-bookings'] });
          }
        } catch (selectelError) {
          console.warn('⚠️ Selectel недоступен, продолжаем сохранение:', selectelError);
          // Продолжаем даже если Selectel недоступен - это НЕ критическая ошибка
        }
      } else if (formData.guest_last_name || formData.guest_first_name) {
        // 🔄 Бывший блок без guest_id — создаём нового гостя в Selectel
        try {
          const response = await supabase.functions.invoke('selectel-proxy', {
            body: {
              operation: 'create_guest',
              hotel_id: currentHotel?.id || booking.hotel_id,
              first_name: formData.guest_first_name,
              last_name: formData.guest_last_name,
              middle_name: formData.guest_middle_name,
              phone: formData.guest_phone,
              email: formData.guest_email || '',
            }
          });

          if (response.data?.guest_id) {
            await supabase
              .from('booking_core')
              .update({ guest_id: response.data.guest_id })
              .eq('id', booking.id);

            booking.guest_id = response.data.guest_id;

            window.dispatchEvent(new CustomEvent('clear-selectel-cache'));
            justSavedRef.current = true;
            await queryClient.invalidateQueries({ queryKey: ['optimized-bookings'] });
            await queryClient.invalidateQueries({ queryKey: ['bookings'] });
            await queryClient.invalidateQueries({ queryKey: ['grid-bookings'] });
            console.log('✅ Гость создан для бывшего блока, guest_id:', response.data.guest_id);
          }
        } catch (createGuestError) {
          console.warn('⚠️ Не удалось создать гостя для блока:', createGuestError);
        }
      }
      
      // 🔐 ВАЖНО: guest_name, guest_phone и guest_email НЕ сохраняются в booking_core!
      // Эти данные хранятся только в Selectel (архитектура 152-ФЗ)
      // guest_name синхронизируется через syncGuestDataToSelectel выше
      const dbUpdates = {
        // guest_name: УДАЛЕНО - нет в booking_core, хранится в Selectel
        // guest_phone: УДАЛЕНО - нет в booking_core
        // guest_email: УДАЛЕНО - нет в booking_core
        check_in_date: formData.check_in_date,
        check_out_date: formData.check_out_date,
        check_in_time: formData.check_in_time,
        check_out_time: formData.check_out_time,
        room_type: formData.room_type, // 🔥 ПОЛНОЕ название категории (например "1Кор 3х м «Комфорт» СБ")
        // ❌ room_category_id НЕ СУЩЕСТВУЕТ в таблице booking_core! Используем только room_type
        room_id: formData.room_id,
        total_amount: formData.total_amount,
        special_requests: formData.special_requests,
        price_locked: formData.price_locked,
        manual_total_amount: formData.manual_total_amount,
        discount_percentage: formData.discount_percentage,
        discount_amount: formData.discount_amount,
        rate_plan_id: formData.rate_plan_id,
        base_price: formData.base_price,
        adult_count: formData.adult_count,
        children_count: formData.children_count,
        infants_count: formData.infants_count,
        cribs_count: formData.cribs_count,
        extra_beds: formData.extra_beds, // 🎯 AK-47 v3: серверный расчёт доплаты
        vip_status: formData.vip_status,
        transfer_needed: formData.transfer_needed,
        dietary_restrictions: formData.dietary_restrictions,
        no_relocation: formData.no_relocation,
        source: formData.source, // 🎯 Источник бронирования
        number_of_guests: (formData.adult_count || 0) + (formData.children_count || 0),
      };
      
      // 🔍 КРИТИЧНО: Детальное логирование перед сохранением для отладки
      console.log('💾 Сохранение бронирования:', {
        bookingId: booking.id,
        changes: {
          room_type: { old: booking.room_type, new: dbUpdates.room_type },
          room_id: { old: booking.room_id, new: dbUpdates.room_id },
          check_in_date: { old: booking.check_in_date, new: dbUpdates.check_in_date },
          check_out_date: { old: booking.check_out_date, new: dbUpdates.check_out_date },
        }
      });
      
      // ✅ Дополнительная валидация перед сохранением
      if (!dbUpdates.room_type || dbUpdates.room_type.trim() === '') {
        console.error('❌ КРИТИЧЕСКАЯ ОШИБКА: room_type пустой!', {
          formData_room_type: formData.room_type,
          formData_room_category_id: formData.room_category_id,
          formData_room_id: formData.room_id
        });
        toast({
          title: "❌ Ошибка",
          description: "Не выбрана категория номера. Пожалуйста, выберите категорию.",
          variant: "destructive",
        });
        setIsSaving(false);
        setIsValidating(false);
        return;
      }

      // 🔄 Автосмена статуса: блок -> подтверждено при вводе имени гостя
      if ((booking as any).is_block && booking.status === 'blocked') {
        const hasGuestName = !!(
          formData.guest_first_name?.trim() || 
          formData.guest_last_name?.trim() || 
          formData.guest_middle_name?.trim()
        );
        if (hasGuestName) {
          (dbUpdates as any).status = 'confirmed';
          (dbUpdates as any).is_block = false;
          console.log('🔄 Блок автоматически переведён в статус "confirmed" (введено имя гостя)');
        }
      }

      // ★ ЗАЩИТА: отклоняем аномальные суммы перед отправкой в RPC
      const MAX_AMOUNT = 99_999_999;
      if ((dbUpdates.total_amount && dbUpdates.total_amount > MAX_AMOUNT) ||
          (dbUpdates.manual_total_amount && dbUpdates.manual_total_amount > MAX_AMOUNT)) {
        console.error('❌ ANOMALY: аномальная сумма отклонена', {
          total_amount: dbUpdates.total_amount,
          manual_total_amount: dbUpdates.manual_total_amount
        });
        toast({
          title: "Ошибка",
          description: "Аномальная сумма. Проверьте данные.",
          variant: "destructive",
        });
        setIsSaving(false);
        setIsValidating(false);
        return;
      }
      
      // Логирование делается атомарно в RPC save_booking_atomic — дублировать не нужно
      
      await onUpdate(dbUpdates as any);
      
      // 🎯 AK-47 v3: НЕ вызываем updateBookingFinancials!
      // save_booking_atomic уже атомарно записал booking_core + booking_financial
      // (включая accommodation_amount и extra_guests_charge для доп. места).
      // Клиент больше НЕ пишет в booking_financial напрямую — единый источник истины.
      // Свежие данные подтянутся через onUpdate -> refetch выше.
      // 🎯 AK-47 v3: сбрасываем флаг взаимодействия — следующее открытие/refetch будет чистым чтением из БД
      hasUserInteractedRef.current = false;
      
      // ✅ Сохранение завершено успешно
      setIsSaved(true);
      onUnsavedChange?.(false);
      const wasBlockConverted = (booking as any).is_block && booking.status === 'blocked' && (dbUpdates as any).status === 'confirmed';
      toast({
        title: wasBlockConverted ? "✅ Блок → Бронирование" : "✅ Сохранено",
        description: wasBlockConverted 
          ? "Блок преобразован в бронирование (статус: Подтверждено)" 
          : "Данные бронирования и финансы успешно обновлены",
      });
      
      // 🔥 AK-47: Broadcast для синхронизации на ВСЕХ компьютерах
      // Это критически важно для времени раннего/позднего заезда на шахматке
      if (broadcastFinancialChange && booking.id) {
        broadcastFinancialChange('UPDATE', booking.id);
        console.log('📡 [BookingBasicInfo] Broadcast отправлен для синхронизации на других компьютерах');
      }
    } catch (error) {
      console.error('❌ Ошибка сохранения:', error);
      toast({
        title: "❌ Ошибка",
        description: error instanceof Error ? error.message : "Не удалось сохранить данные",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
      setIsSaving(false);
    }
  };

  // 🔗 Передаём handleSave наверх для диалога "Несохранённые изменения"
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onSaveRef?.(handleSave);
  });

  // 🚫 Аннулирование бронирования с причиной
  const handleCancelBooking = async () => {
    if (!cancellationReason) return;
    setIsCancelling(true);
    try {
      const reasonText = cancellationComment 
        ? `${cancellationReason}: ${cancellationComment}` 
        : cancellationReason;
      await onUpdate({ 
        status: 'cancelled', 
        cancelled_at: new Date().toISOString(),
        cancellation_reason: reasonText 
      } as any);
      setShowCancelDialog(false);
      setCancellationReason('');
      setCancellationComment('');
      toast({
        title: "✅ Бронирование аннулировано",
        description: `Причина: ${cancellationReason}`,
      });
    } catch (error) {
      console.error('❌ Ошибка аннулирования:', error);
      toast({
        title: "❌ Ошибка",
        description: "Не удалось аннулировать бронирование",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // 🔄 Восстановление брони
  const handleRestoreBooking = async () => {
    if (!restoreRoomId) return;
    setIsRestoring(true);
    try {
      const selectedRoom = rooms.find(r => r.id === restoreRoomId);
      const selectedCategory = categories.find(c => c.id === restoreCategoryId);
      await onUpdate({
        status: 'confirmed',
        room_id: restoreRoomId,
        room_type: selectedCategory?.name || booking.room_type,
        cancelled_at: null,
        cancellation_reason: null,
      } as any);
      setShowRestoreDialog(false);
      setRestoreRoomId('');
      setRestoreCategoryId('');
      toast({
        title: "✅ Бронирование восстановлено",
        description: `Номер ${selectedRoom?.room_number || ''} назначен, статус: Подтверждено`,
      });
    } catch (error) {
      console.error('❌ Ошибка восстановления:', error);
      toast({
        title: "❌ Ошибка",
        description: "Не удалось восстановить бронирование",
        variant: "destructive",
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // Конфликтующие бронирования для номеров при восстановлении
  const [restoreConflicts, setRestoreConflicts] = useState<Record<string, { check_in_date: string; check_out_date: string }[]>>({});

  // Получить ВСЕ номера категории + проверить пересечения дат
  const restoreAvailableRooms = useMemo(() => {
    if (!restoreCategoryId || !rooms.length) return [];
    return rooms.filter(r => r.room_category_id === restoreCategoryId);
  }, [restoreCategoryId, rooms]);

  // Открыть диалог восстановления — загружаем конфликты
  const openRestoreDialog = async () => {
    const currentRoom = rooms.find(r => r.id === booking.room_id);
    const defaultCategoryId = currentRoom?.room_category_id || 
      categories.find(c => c.name === booking.room_type)?.id || '';
    setRestoreCategoryId(defaultCategoryId);
    setRestoreRoomId('');
    setRestoreConflicts({});
    setShowRestoreDialog(true);
    
    // Загружаем конфликты для номеров этой категории
    if (defaultCategoryId) {
      await fetchRestoreConflicts(defaultCategoryId);
    }
  };

  // Загрузить конфликтующие бронирования для номеров категории
  const fetchRestoreConflicts = async (categoryId: string) => {
    const categoryRoomIds = rooms
      .filter(r => r.room_category_id === categoryId)
      .map(r => r.id);
    if (categoryRoomIds.length === 0) return;
    
    try {
      const { data: conflicting } = await supabase
        .from('booking_core')
        .select('room_id, check_in_date, check_out_date')
        .in('room_id', categoryRoomIds)
        .in('status', ['confirmed', 'checked_in'])
        .lt('check_in_date', booking.check_out_date)
        .gt('check_out_date', booking.check_in_date)
        .is('deleted_at', null);
      
      const conflicts: Record<string, { check_in_date: string; check_out_date: string }[]> = {};
      (conflicting || []).forEach(c => {
        if (!c.room_id) return;
        if (!conflicts[c.room_id]) conflicts[c.room_id] = [];
        conflicts[c.room_id].push({ check_in_date: c.check_in_date, check_out_date: c.check_out_date });
      });
      setRestoreConflicts(conflicts);
    } catch (err) {
      console.error('Ошибка загрузки конфликтов:', err);
    }
  };

  // 📋 Изменение статуса бронирования с проверкой конфликтов
  const handleStatusChange = async (newStatus: string, forceEarlyCheckin: boolean = false) => {
    try {
      // 🚨 Защита от случайного раннего заселения
      if (newStatus === 'checked_in' && !forceEarlyCheckin) {
        const checkInDate = new Date(booking.check_in_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        checkInDate.setHours(0, 0, 0, 0);
        
        const daysUntilCheckIn = Math.floor(
          (checkInDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        
        // Допуск: 1 день раннего заезда — OK (гость приехал накануне)
        // 2+ дней — требует подтверждения
        if (daysUntilCheckIn > 1) {
          setEarlyCheckinDays(daysUntilCheckIn);
          setShowEarlyCheckinDialog(true);
          return; // Ждём подтверждения
        }
      }
      
      // 🔥 AK-47: Предварительная проверка конфликтов перед заселением
      if (newStatus === 'checked_in' && booking.room_id) {
        console.log('🔍 Проверяем конфликты перед заселением...');
        
        const { data: conflictData, error: conflictError } = await supabase.rpc('check_booking_conflict_atomic', {
          p_room_id: booking.room_id,
          p_check_in_date: new Date(booking.check_in_date).toISOString(),
          p_check_out_date: new Date(booking.check_out_date).toISOString(),
          p_exclude_booking_id: booking.id
        });
        
        if (!conflictError && conflictData && conflictData[0]?.has_conflict) {
          const conflict = conflictData[0];
          toast({
            variant: "destructive",
            title: "🚫 Заселение невозможно",
            description: `Номер занят гостем "${conflict.conflicting_guest_name || 'Неизвестный гость'}". Возможно ранний заезд или поздний выезд с буфером уборки. Проверьте расписание или выберите другой номер.`,
          });
          return; // Прерываем без ошибки
        }
      }
      
      // АК-47: При смене статуса блока на любой другой — сбрасываем флаг блока
      const updatePayload: any = { status: newStatus };
      if ((booking as any).is_block && newStatus !== 'blocked') {
        updatePayload.is_block = false;
        updatePayload.block_reason = null;
        // АК-47: При конвертации блока — сбросить старую цену,
        // чтобы save_booking_atomic пересчитал по текущим тарифам
        updatePayload.price_locked = false;
        updatePayload.manual_total_amount = null;
        updatePayload.total_amount = null;
      }
      await onUpdate(updatePayload);
      // 🧹 AK-47: заезд/выезд напрямую влияют на доску уборки — форсим свежий refetch,
      // чтобы горничные видели актуальный статус мгновенно (без ожидания 15с polling).
      if (newStatus === 'checked_in' || newStatus === 'checked_out') {
        const hkHotelId = currentHotel?.id || booking.hotel_id;
        if (hkHotelId) {
          queryClient.invalidateQueries({ queryKey: ['housekeeping-board', hkHotelId] });
        }
      }
      toast({
        title: "✅ Статус обновлен",
        description: `Статус бронирования изменен на '${getStatusLabel(newStatus)}'`,
      });
    } catch (error: any) {
      console.error('❌ Ошибка изменения статуса:', error);
      
      // 🔥 AK-47: Парсинг TIME_CONFLICT для понятного сообщения
      const errorMessage = error?.message || '';
      if (errorMessage.includes('TIME_CONFLICT')) {
        toast({
          variant: "destructive",
          title: "🚫 Конфликт бронирования",
          description: "Номер занят другим гостем с учётом буфера уборки (120 мин). Проверьте расписание или выберите другой номер.",
        });
        return;
      }
      
      toast({
        title: "❌ Ошибка",
        description: "Не удалось изменить статус бронирования",
        variant: "destructive",
      });
    }
  };

  // 🗑️ Мягкое удаление бронирования (для отменённых с возвратом)
  const handleSoftDelete = async () => {
    if (!booking.id || !currentHotel) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('booking_core')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: (await supabase.auth.getUser()).data.user?.id,
          deletion_reason: 'Удалено после возврата средств'
        })
        .eq('id', booking.id)
        .eq('hotel_id', currentHotel.id);

      if (error) throw error;

      toast({
        title: "✅ Бронирование удалено",
        description: "Бронирование перемещено в корзину. Восстановление возможно в течение 30 дней.",
      });

      // Переход на страницу удалённых бронирований
      navigate('/admin/deleted-bookings');
    } catch (error) {
      console.error('❌ Ошибка удаления:', error);
      toast({
        title: "❌ Ошибка",
        description: "Не удалось удалить бронирование",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // 🏷️ Получить читаемое название статуса
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'pending': 'Ожидает',
      'confirmed': 'Подтверждено',
      'checked_in': 'Заселено',
      'checked_out': 'Выселено',
      'cancelled': 'Отменено',
    };
    return labels[status] || status;
  };

  const nights = formData.check_in_date && formData.check_out_date
    ? differenceInDays(new Date(formData.check_out_date), new Date(formData.check_in_date))
    : 0;

  // 💰 Расчёт услуг для финансовой сводки
  const totalServicesAmount = booking.services?.reduce((sum, service) => sum + service.total_price, 0) || 0;
  const totalWithServices = (formData.total_amount || booking.total_amount) + totalServicesAmount;
  
  return (
    <div className="space-y-4">
      {/* 💰 Финансовая сводка - СВЕРХУ по требованию */}
       <Card 
          className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent cursor-pointer hover:border-primary/40 transition-colors"
          onClick={() => onTabChange?.('financial')}
        >
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CreditCard className="h-5 w-5 text-primary" />
            Финансовая информация
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            // 🔥 AK-47: Рассчитываем АКТУАЛЬНЫЕ финансовые данные из платежей
            const payments = booking.payments || [];
            const actualPaid = payments
              .filter(p => p.payment_type !== 'refund' && p.status === 'completed')
              .reduce((sum, p) => sum + p.amount, 0);
            const actualRefunded = payments
              .filter(p => p.payment_type === 'refund' && p.status === 'completed')
              .reduce((sum, p) => sum + p.amount, 0);
            
            // Чистая оплаченная сумма = платежи - возвраты
            const netPaid = actualPaid - actualRefunded;
            // Остаток = итого с услугами - чистая оплата
            const actualBalance = totalWithServices - netPaid;
            
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="text-center p-3 bg-background rounded-lg border">
                    <div className="text-xl font-bold">{(formData.total_amount || booking.total_amount).toLocaleString('ru-RU')} ₽</div>
                    <div className="text-xs text-muted-foreground">
                      Проживание
                      {(formData.extra_guests_charge ?? 0) > 0 && (
                        <span className="block text-[10px] text-primary mt-0.5">
                          вкл. доп. место +{Math.round(formData.extra_guests_charge).toLocaleString('ru-RU')} ₽
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg border">
                    <div className="text-xl font-bold text-blue-600">{totalServicesAmount.toLocaleString('ru-RU')} ₽</div>
                    <div className="text-xs text-muted-foreground">Услуги ({booking.services?.length || 0})</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg border">
                    <div className="text-xl font-bold text-primary">{totalWithServices.toLocaleString('ru-RU')} ₽</div>
                    <div className="text-xs text-muted-foreground">Итого к оплате</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg border">
                    <div className="text-xl font-bold text-green-600">{netPaid.toLocaleString('ru-RU')} ₽</div>
                    <div className="text-xs text-muted-foreground">
                      Оплачено {actualRefunded > 0 && <span className="text-destructive text-[10px]">(-{actualRefunded.toLocaleString('ru-RU')})</span>}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg border">
                    <div className={cn(
                      "text-xl font-bold",
                      actualBalance > 0 ? "text-destructive" : "text-muted-foreground"
                    )}>
                      {actualBalance.toLocaleString('ru-RU')} ₽
                    </div>
                    <div className="text-xs text-muted-foreground">Остаток</div>
                  </div>
                </div>
                
                {/* 🛏️ Доп. место — отдельная строка детализации */}
                {(formData.extra_guests_charge ?? 0) > 0 && (
                  <div className="flex justify-between items-center pt-2 border-t text-sm">
                    <span className="text-muted-foreground">🛏️ Доп. место:</span>
                    <span className="font-semibold text-primary">
                      +{Math.round(formData.extra_guests_charge).toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                )}

                {/* ⏰ Доплаты за ранний заезд / поздний выезд */}
                {((formData.early_checkin_surcharge || 0) > 0 || (formData.late_checkout_surcharge || 0) > 0) && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {(formData.early_checkin_surcharge || 0) > 0 && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        ⚡ Ранний заезд: +{formData.early_checkin_surcharge.toLocaleString('ru-RU')} ₽
                      </Badge>
                    )}
                    {(formData.late_checkout_surcharge || 0) > 0 && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        🌙 Поздний выезд: +{formData.late_checkout_surcharge.toLocaleString('ru-RU')} ₽
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>



      {/* 👤 Информация о госте */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Информация о госте
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {/* 🎯 Порядок: Фамилия → Имя → Отчество (русский стандарт) */}
            <div>
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Фамилия *
              </Label>
              <Input
                value={formData.guest_last_name}
                onChange={(e) => {
                  const lastName = e.target.value;
                  handleChange({ 
                    guest_last_name: lastName,
                    guest_name: `${lastName} ${formData.guest_first_name} ${formData.guest_middle_name}`.trim()
                  });
                }}
                required
                className={cn(validationErrors.guest_name && "border-destructive")}
              />
              {booking.selectel_guest_data && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Из Selectel CRM
                </p>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Имя *
              </Label>
              <Input
                value={formData.guest_first_name}
                onChange={(e) => {
                  const firstName = e.target.value;
                  handleChange({ 
                    guest_first_name: firstName,
                    guest_name: `${formData.guest_last_name} ${firstName} ${formData.guest_middle_name}`.trim()
                  });
                }}
                required
                className={cn(validationErrors.guest_name && "border-destructive")}
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Отчество
              </Label>
              <Input
                value={formData.guest_middle_name}
                onChange={(e) => {
                  const middleName = e.target.value;
                  handleChange({ 
                    guest_middle_name: middleName,
                    guest_name: `${formData.guest_last_name} ${formData.guest_first_name} ${middleName}`.trim()
                  });
                }}
                className={cn(validationErrors.guest_name && "border-destructive")}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Телефон * (79999999999)
              </Label>
              <Input
                value={formData.guest_phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 11) {
                    handleChange({ guest_phone: value });
                  }
                }}
                onBlur={(e) => {
                  const phone = e.target.value.replace(/\D/g, '');
                  if (phone && phone.length === 11 && phone.startsWith('7')) {
                    // Valid format
                  } else if (phone && (phone.length !== 11 || !phone.startsWith('7'))) {
                    handleChange({ guest_phone: '' });
                    toast({
                      title: "Неверный формат телефона",
                      description: "Телефон должен быть в формате 79999999999",
                      variant: "destructive"
                    });
                  }
                }}
                placeholder="79999999999"
                maxLength={11}
                required
                className={cn(validationErrors.guest_phone && "border-destructive")}
              />
              {validationErrors.guest_phone && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.guest_phone}
                </p>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                type="email"
                value={formData.guest_email}
                onChange={(e) => handleChange({ guest_email: e.target.value })}
                placeholder=""
                className={cn(validationErrors.guest_email && "border-destructive")}
              />
              {validationErrors.guest_email && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.guest_email}
                </p>
              )}
            </div>
          </div>
          
          {/* 💬 Комментарии под информацией о госте */}
          <div className="mt-4">
            <Label className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Комментарии
            </Label>
            <Textarea
              value={formData.special_requests}
              onChange={(e) => handleChange({ special_requests: e.target.value })}
              placeholder="Любые дополнительные пожелания гостя..."
              className="mt-1 min-h-[60px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* 📅 Даты и время */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Даты проживания
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Дата заезда *</Label>
              <div className="flex gap-2 mt-1">
                <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 justify-start text-left font-normal",
                        !formData.check_in_date && "text-muted-foreground",
                        validationErrors.check_in_date && "border-destructive"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.check_in_date ? (
                        format(new Date(formData.check_in_date), 'd MMMM yyyy г.', { locale: ru })
                      ) : (
                        <span>Выберите дату</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <UICalendar
                      mode="single"
                      selected={formData.check_in_date ? new Date(formData.check_in_date) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          handleChange({ check_in_date: format(date, 'yyyy-MM-dd') });
                          setCheckInOpen(false);
                        }
                      }}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      locale={ru}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <Select value={formData.check_in_time} onValueChange={(value) => handleChange({ check_in_time: value })}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="00:00">00:00</SelectItem>
                    <SelectItem value="01:00">01:00</SelectItem>
                    <SelectItem value="02:00">02:00</SelectItem>
                    <SelectItem value="03:00">03:00</SelectItem>
                    <SelectItem value="04:00">04:00</SelectItem>
                    <SelectItem value="05:00">05:00</SelectItem>
                    <SelectItem value="06:00">06:00</SelectItem>
                    <SelectItem value="07:00">07:00</SelectItem>
                    <SelectItem value="08:00">08:00</SelectItem>
                    <SelectItem value="09:00">09:00</SelectItem>
                    <SelectItem value="10:00">10:00</SelectItem>
                    <SelectItem value="11:00">11:00</SelectItem>
                    <SelectItem value="12:00">12:00</SelectItem>
                    <SelectItem value="13:00">13:00</SelectItem>
                    <SelectItem value="14:00">14:00</SelectItem>
                    <SelectItem value="15:00">15:00 ✨</SelectItem>
                    <SelectItem value="16:00">16:00</SelectItem>
                    <SelectItem value="17:00">17:00</SelectItem>
                    <SelectItem value="18:00">18:00</SelectItem>
                    <SelectItem value="19:00">19:00</SelectItem>
                    <SelectItem value="20:00">20:00</SelectItem>
                    <SelectItem value="21:00">21:00</SelectItem>
                    <SelectItem value="22:00">22:00</SelectItem>
                    <SelectItem value="23:00">23:00</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {validationErrors.check_in_date && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.check_in_date}
                </p>
              )}
            </div>
            <div>
              <Label>Дата выезда *</Label>
              <div className="flex gap-2 mt-1">
                <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 justify-start text-left font-normal",
                        !formData.check_out_date && "text-muted-foreground",
                        validationErrors.check_out_date && "border-destructive"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.check_out_date ? (
                        format(new Date(formData.check_out_date), 'd MMMM yyyy г.', { locale: ru })
                      ) : (
                        <span>Выберите дату</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                    <UICalendar
                      mode="single"
                      selected={formData.check_out_date ? new Date(formData.check_out_date) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          handleChange({ check_out_date: format(date, 'yyyy-MM-dd') });
                          setCheckOutOpen(false);
                        }
                      }}
                      fromDate={
                        formData.check_in_date 
                          ? new Date(new Date(formData.check_in_date).getTime() + 24 * 60 * 60 * 1000) // +1 день от заезда
                          : new Date() // Или сегодня если заезд не выбран
                      }
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        if (!formData.check_in_date) {
                          // Если заезд не выбран - блокируем прошлые даты
                          return date < today;
                        }
                        
                        // Дата выезда должна быть минимум на следующий день после заезда
                        const checkInDate = new Date(formData.check_in_date);
                        checkInDate.setHours(0, 0, 0, 0);
                        const minCheckOut = new Date(checkInDate.getTime() + 24 * 60 * 60 * 1000);
                        
                        return date < minCheckOut;
                      }}
                      initialFocus
                      locale={ru}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <Select value={formData.check_out_time} onValueChange={(value) => handleChange({ check_out_time: value })}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="00:00">00:00</SelectItem>
                    <SelectItem value="01:00">01:00</SelectItem>
                    <SelectItem value="02:00">02:00</SelectItem>
                    <SelectItem value="03:00">03:00</SelectItem>
                    <SelectItem value="04:00">04:00</SelectItem>
                    <SelectItem value="05:00">05:00</SelectItem>
                    <SelectItem value="06:00">06:00</SelectItem>
                    <SelectItem value="07:00">07:00</SelectItem>
                    <SelectItem value="08:00">08:00</SelectItem>
                    <SelectItem value="09:00">09:00</SelectItem>
                    <SelectItem value="10:00">10:00</SelectItem>
                    <SelectItem value="11:00">11:00</SelectItem>
                    <SelectItem value="12:00">12:00 ✨</SelectItem>
                    <SelectItem value="13:00">13:00</SelectItem>
                    <SelectItem value="14:00">14:00</SelectItem>
                    <SelectItem value="15:00">15:00</SelectItem>
                    <SelectItem value="16:00">16:00</SelectItem>
                    <SelectItem value="17:00">17:00</SelectItem>
                    <SelectItem value="18:00">18:00</SelectItem>
                    <SelectItem value="19:00">19:00</SelectItem>
                    <SelectItem value="20:00">20:00</SelectItem>
                    <SelectItem value="21:00">21:00</SelectItem>
                    <SelectItem value="22:00">22:00</SelectItem>
                    <SelectItem value="23:00">23:00</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {validationErrors.check_out_date && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.check_out_date}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
            <Calendar className="w-5 h-5 text-primary" />
            <span className="font-semibold">
              {nights > 0 ? (
                `${nights} ${nights === 1 ? 'ночь' : nights < 5 ? 'ночи' : 'ночей'}`
              ) : (
                <span className="text-destructive">Некорректные даты</span>
              )}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* 🏨 Номер и тариф */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <DoorOpen className="w-5 h-5 text-primary" />
            Номер и тариф
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Категория номера *</Label>
              <Select 
                value={formData.room_category_id || ''} 
                onValueChange={(value) => {
                  // 🎯 Этап 2.2: Используем новую функцию проверки доступности (по category ID)
                  handleRoomTypeChange(value);
                }}
              >
                <SelectTrigger className={cn("mt-1", validationErrors.room_type && "border-destructive")}>
                  <SelectValue placeholder="Выберите категорию" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesLoading ? (
                    <SelectItem value="loading" disabled>⏳ Загрузка...</SelectItem>
                  ) : categories.length === 0 ? (
                    <SelectItem value="none" disabled>Нет категорий</SelectItem>
                  ) : (
                    categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {validationErrors.room_type && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.room_type}
                </p>
              )}
            </div>
            
            <div>
              <Label>Конкретный номер</Label>
              <Select 
                value={formData.room_id || ''} 
                onValueChange={(value) => {
                  console.log('🔄 Выбран номер:', value);
                  // 🔥 КРИТИЧНО: При выборе номера обновляем room_type ПОЛНЫМ названием категории
                  const selectedRoom = rooms.find(r => r.id === value);
                  if (selectedRoom) {
                    const category = categories.find(c => c.id === selectedRoom.room_category_id);
                    if (category) {
                      console.log('✅ Выбран номер:', selectedRoom.room_number, 'категория:', category.name);
                      handleChange({ 
                        room_id: value,
                        room_type: category.name, // ✅ ПОЛНОЕ название категории из БД!
                        room_category_id: selectedRoom.room_category_id // ✅ Только для formData, НЕ для БД
                      });
                    } else {
                      console.error('❌ Категория не найдена для номера:', selectedRoom.room_number);
                      handleChange({ room_id: value });
                    }
                  } else {
                    handleChange({ room_id: value });
                  }
                }}
                disabled={!formData.room_type}
              >
                <SelectTrigger className={cn(
                  "mt-1", 
                  validationErrors.room_id && "border-destructive"
                )}>
                  <SelectValue>
                    {(() => {
                      if (!formData.room_category_id) {
                        return "Сначала выберите категорию";
                      }
                      if (roomsLoading) {
                        return "⏳ Загрузка...";
                      }
                      if (selectedRoom) {
                        return `№${selectedRoom.room_number} - ${selectedRoom.name || selectedRoom.room_type}`;
                      }
                      if (formData.room_id) {
                        return `Номер ${formData.room_id} (загрузка...)`;
                      }
                      return "Выберите номер";
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {roomsLoading ? (
                    <SelectItem value="loading" disabled>⏳ Загрузка...</SelectItem>
                  ) : categoryRooms.length === 0 ? (
                    <SelectItem value="none" disabled>
                      Нет номеров в выбранной категории
                    </SelectItem>
                  ) : (
                    <>
                      {categoryRooms.map((room) => {
                        const row = availabilityByRoom[room.id];
                        const isCurrent = room.id === formData.room_id;
                        // AK-47: без серверного ответа НЕ утверждаем «Доступен»
                        let statusIcon = '⏳';
                        let statusText = 'Проверяем…';
                        if (availabilityInvalidRange) {
                          statusIcon = '📅';
                          statusText = 'Укажите корректные даты';
                        } else if (availabilityFailed) {
                          statusIcon = '⚠️';
                          statusText = 'Проверка недоступна';
                        } else if (row && !isCheckingAvailability) {
                          if (row.is_available) {
                            statusIcon = '✅';
                            statusText = 'Доступен';
                          } else {
                            statusIcon = '🔒';
                            statusText = conflictText(row) || 'Занят';
                          }
                        }
                        const blocked = Boolean(row && !row.is_available && !isCheckingAvailability);
                        return (
                          <SelectItem 
                            key={room.id} 
                            value={room.id}
                            disabled={blocked && !isCurrent}
                          >
                            {statusIcon} №{room.room_number} - {room.name || room.room_type} ({statusText})
                          </SelectItem>
                        );
                      })}
                    </>
                  )}
                </SelectContent>
              </Select>
              {validationErrors.room_id && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.room_id}
                </p>
              )}
              {selectedRoom && (
                <p className="text-xs text-muted-foreground mt-1">
                  👥 Вместимость: {selectedRoom.capacity} чел.
                </p>
              )}
            </div>

            <div>
              <Label>Тарифный план *</Label>
              <Select 
                value={formData.rate_plan_id || ''} 
                onValueChange={(value) => handleChange({ rate_plan_id: value })}
              >
                <SelectTrigger className={cn("mt-1", validationErrors.rate_plan_id && "border-destructive")}>
                  <SelectValue placeholder="Выберите тариф" />
                </SelectTrigger>
                <SelectContent>
                  {ratePlansLoading ? (
                    <SelectItem value="loading" disabled>⏳ Загрузка...</SelectItem>
                  ) : ratePlans.length === 0 ? (
                    <SelectItem value="none" disabled>Нет тарифов</SelectItem>
                  ) : (
                    ratePlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name}
                        {plan.is_default && <span className="ml-2 text-xs text-primary">✨ По умолчанию</span>}
                        {(plan as any).price_modifier !== undefined && (plan as any).price_modifier !== 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {(plan as any).price_modifier > 0 ? '+' : ''}{(plan as any).price_modifier}%
                          </span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {validationErrors.rate_plan_id && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.rate_plan_id}
                </p>
              )}
              {selectedRatePlan && (
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedRatePlan.description}
                </p>
              )}
            </div>

            {/* 📝 Источник бронирования */}
            <div>
              <Label>Источник</Label>
              <Select 
                value={formData.source || 'manual'} 
                onValueChange={(value) => handleChange({ source: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Выберите источник" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="widget">🌐 Виджет</SelectItem>
                  <SelectItem value="manual">✏️ Ручное</SelectItem>
                  <SelectItem value="direct">📞 Прямое</SelectItem>
                  <SelectItem value="booking_com">🏷️ Booking.com</SelectItem>
                  <SelectItem value="booking_module">📋 Модуль бронирования</SelectItem>
                  <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
                  <SelectItem value="expedia">🌍 Expedia</SelectItem>
                  <SelectItem value="airbnb">🏠 Airbnb</SelectItem>
                  <SelectItem value="phone">☎️ Телефон</SelectItem>
                  <SelectItem value="travel3d_widget">🔗 Travel3D</SelectItem>
                  <SelectItem value="travel3d">🔗 Travel3D API</SelectItem>
                  <SelectItem value="ostrovok">🏝️ Островок</SelectItem>
                  <SelectItem value="yandex_travel">🚂 Яндекс Путешествия</SelectItem>
                  <SelectItem value="other">📌 Другое</SelectItem>
                  {/* Fallback: показать неизвестный источник, чтобы поле не было пустым */}
                  {formData.source &&
                    !['widget','manual','direct','booking_com','booking_module','whatsapp','expedia','airbnb','phone','travel3d_widget','travel3d','ostrovok','yandex_travel','other'].includes(formData.source) && (
                    <SelectItem value={formData.source}>
                      🔌 {String(formData.source).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>

              {/* 🛏️ Доп. место — независимый переключатель (не зависит от количества гостей) */}
              <div className="flex items-center gap-2 mt-3 p-2 rounded-md border bg-muted/30">
                <Checkbox
                  id="extra-bed-toggle"
                  checked={(formData.extra_beds ?? 0) > 0}
                  onCheckedChange={(checked) => {
                    handleChange({ extra_beds: checked ? 1 : 0 });
                  }}
                />
                <Label htmlFor="extra-bed-toggle" className="cursor-pointer text-sm font-normal">
                  🛏️ Доп. место
                  {(formData.extra_guests_charge ?? 0) > 0 && (
                    <span className="ml-2 text-xs text-primary">
                      (+{Math.round(formData.extra_guests_charge).toLocaleString('ru-RU')} ₽)
                    </span>
                  )}
                </Label>
              </div>
            </div>
          </div>

          {/* 👥 Количество гостей */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Взрослые
              </Label>
              <Input
                type="number"
                value={formData.adult_count ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  handleChange({ adult_count: val === '' ? null : Number(val) });
                }}
                onBlur={(e) => {
                  // При потере фокуса устанавливаем минимум 1 для взрослых
                  if (!e.target.value || Number(e.target.value) < 1) {
                    handleChange({ adult_count: 1 });
                  }
                }}
                min="1"
                max="10"
                className="mt-1"
                placeholder="1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Дети (2-12 лет)
              </Label>
              <Input
                type="number"
                value={formData.children_count ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  handleChange({ children_count: val === '' ? null : Number(val) });
                }}
                onBlur={(e) => {
                  // При потере фокуса устанавливаем 0 если пусто
                  if (e.target.value === '') {
                    handleChange({ children_count: 0 });
                  }
                }}
                min="0"
                max="10"
                className="mt-1"
                placeholder="0"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Младенцы (0-2 года)
              </Label>
              <Input
                type="number"
                value={formData.infants_count ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  handleChange({ infants_count: val === '' ? null : Number(val) });
                }}
                onBlur={(e) => {
                  // При потере фокуса устанавливаем 0 если пусто
                  if (e.target.value === '') {
                    handleChange({ infants_count: 0 });
                  }
                }}
                min="0"
                max="10"
                className="mt-1"
                placeholder="0"
              />
            </div>
          </div>
          
          {/* 📊 Тарификация по дням */}
          <div className="mt-4 pt-3 border-t">
            <div className="flex items-center justify-between mb-3">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <Calendar className="w-4 h-4 text-primary" />
                Тарификация по дням
              </Label>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Цена за ночь</div>
                  {isLoadingRates ? (
                    <div className="flex items-center gap-2 justify-end">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Расчёт...</span>
                    </div>
                  ) : (
                    <div className="text-lg font-bold">
                      {dailyRates.length > 0 
                        ? `${Math.round(dailyRates.reduce((s, r) => s + r.price, 0) / dailyRates.length).toLocaleString()} ₽` 
                        : '—'}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDailyRates(!showDailyRates)}
                >
                  {showDailyRates ? (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Свернуть
                    </>
                  ) : (
                    <>
                      <ChevronRight className="h-4 w-4 mr-1" />
                      Подробнее ({nights} ночей)
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {showDailyRates && (
              <div className="space-y-4">
                {isLoadingRates ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <span className="ml-3 text-muted-foreground">Загрузка тарифов...</span>
                  </div>
                ) : dailyRates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Нет данных о тарифах для этого бронирования</p>
                  </div>
                ) : (
                  <>
                    {/* Сводка */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="text-lg font-bold">{nights}</div>
                        <div className="text-xs text-muted-foreground">Ночей</div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="text-lg font-bold">
                          {dailyRates.reduce((sum, r) => sum + r.price, 0).toLocaleString()} ₽
                        </div>
                        <div className="text-xs text-muted-foreground">Сумма по тарифам</div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="text-lg font-bold">
                          {Math.round(dailyRates.reduce((s, r) => s + r.price, 0) / dailyRates.length).toLocaleString()} ₽
                        </div>
                        <div className="text-xs text-muted-foreground">Средняя за ночь</div>
                      </div>
                    </div>

                    {/* Таблица с датами */}
                    <div className="max-h-64 overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background">
                          <TableRow>
                            <TableHead>Дата</TableHead>
                            <TableHead>Сезон / Тариф</TableHead>
                            <TableHead className="text-right">Цена</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyRates.map((rate) => (
                            <TableRow key={rate.date}>
                              <TableCell className="font-medium">
                                {new Date(rate.date).toLocaleDateString('ru-RU', { 
                                  weekday: 'short', 
                                  day: 'numeric', 
                                  month: 'short' 
                                })}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <Badge variant="secondary" className="w-fit text-xs">
                                    {rate.seasonName || 'Базовый'}
                                  </Badge>
                                  {rate.ratePlanName && (
                                    <span className="text-xs text-muted-foreground">{rate.ratePlanName}</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {rate.price > 0 
                                  ? `${rate.price.toLocaleString()} ₽`
                                  : <span className="text-destructive">Не настроена</span>
                                }
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 💰 Финансы (Риксос стандарт) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Финансовая информация
            {formData.price_locked && (
              <Badge variant="outline" className="ml-2">
                <Lock className="w-3 h-3 mr-1" />
                Зафиксирована
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <Percent className="w-3 h-3" />
                Скидка (%)
              </Label>
              <Input
                type="number"
                value={formData.discount_percentage}
                onChange={(e) => handleChange({ discount_percentage: Number(e.target.value) })}
                min="0"
                max="100"
                step="0.1"
                disabled={formData.price_locked}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Скидка (сумма, ₽)</Label>
              <Input
                type="number"
                value={formData.discount_amount}
                onChange={(e) => handleChange({ discount_amount: Number(e.target.value) })}
                min="0"
                step="100"
                disabled={formData.price_locked}
                className="mt-1"
              />
            </div>
          </div>
          
          {/* 📊 Детализация расчета - цены ТОЛЬКО из get_rate RPC (no fallback!) */}
          <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
            {(() => {
              // 🔥 AK-47: Правильный расчёт средней цены за ночь
              // Используем dailyRates если есть, иначе fallback на formData
              const avgPricePerNight = dailyRates.length > 0
                ? dailyRates.reduce((s, r) => s + r.price, 0) / dailyRates.length
                : (nights > 0 ? formData.total_amount / nights : 0);
              const baseTotal = dailyRates.length > 0
                ? dailyRates.reduce((s, r) => s + r.price, 0)
                : formData.total_amount;
              
              return (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Расчётная цена за ночь:</span>
                    <span className="font-medium">
                      {Math.round(avgPricePerNight).toLocaleString()} ₽
                    </span>
                  </div>
                  
                  {selectedRatePlan && (selectedRatePlan as any).price_modifier !== undefined && (selectedRatePlan as any).price_modifier !== 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Тариф "{selectedRatePlan.name}" ({(selectedRatePlan as any).price_modifier > 0 ? '+' : ''}{(selectedRatePlan as any).price_modifier}%):
                      </span>
                      <span className="font-medium">
                        Применён к расчёту
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Количество ночей:</span>
                    <span className="font-medium">{nights}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="text-muted-foreground">Базовая стоимость:</span>
                    {isPriceLoading ? (
                      <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                    ) : (
                      /* Базовая стоимость = сумма дневных тарифов ИЛИ total_amount */
                      <span className="font-semibold">{Math.round(baseTotal).toLocaleString()} ₽</span>
                    )}
                  </div>
                </>
              );
            })()}

            {(formData.extra_guests_charge ?? 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Доп. место:</span>
                <span className="font-medium text-foreground">
                  +{Math.round(formData.extra_guests_charge).toLocaleString('ru-RU')} ₽
                </span>
              </div>
            )}
            
            {(formData.discount_percentage > 0 || formData.discount_amount > 0) && (() => {
              // Скидка: разница между базовой стоимостью и итого
              const discountAmount = (dailyRates.length > 0
                ? dailyRates.reduce((s, r) => s + r.price, 0)
                : formData.total_amount) - formData.total_amount;
              return discountAmount > 0 ? (
                <div className="flex justify-between text-sm text-destructive">
                  <span>Скидка:</span>
                  {isPriceLoading ? (
                    <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                  ) : (
                    <span>-{Math.round(discountAmount).toLocaleString()} ₽</span>
                  )}
                </div>
              ) : null;
            })()}
            
            <div className="flex justify-between text-lg font-bold border-t pt-2">
              <span>Итого к оплате:</span>
              {isPriceLoading ? (
                <div className="h-6 w-24 bg-primary/20 animate-pulse rounded" />
              ) : (
                <span className="text-primary">{formData.total_amount.toLocaleString()} ₽</span>
              )}
            </div>
            
            {/* 💳 Прогресс оплаты */}
            {(() => {
              // 🔥 AK-47: Рассчитываем АКТУАЛЬНЫЕ финансовые данные из платежей
              const payments = booking.payments || [];
              const actualPaid = payments
                .filter(p => p.payment_type !== 'refund' && p.status === 'completed')
                .reduce((sum, p) => sum + p.amount, 0);
              const actualRefunded = payments
                .filter(p => p.payment_type === 'refund' && p.status === 'completed')
                .reduce((sum, p) => sum + p.amount, 0);
              
              // Чистая оплаченная сумма = платежи - возвраты
              const netPaid = actualPaid - actualRefunded;
              // Остаток
              const actualBalance = formData.total_amount - netPaid;
              const progressPercent = formData.total_amount > 0 ? Math.min(100, (netPaid / formData.total_amount) * 100) : 0;
              
              if (!booking.financial && payments.length === 0) return null;
              
              return (
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Оплачено:</span>
                    <span className="font-medium text-green-600">
                      {netPaid.toLocaleString()} ₽
                      {actualRefunded > 0 && <span className="text-destructive text-xs ml-1">(-{actualRefunded.toLocaleString()})</span>}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Остаток:</span>
                    <span className="font-medium text-orange-600">
                      {actualBalance.toLocaleString()} ₽
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* 💰 Поле для ручной установки фиксированной цены */}
          {formData.price_locked && (
            <div>
              <Label>Фиксированная стоимость брони (₽)</Label>
              <Input
                type="number"
                value={formData.manual_total_amount ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Пустое поле = сброс фиксации суммы, без записи "0" в БД
                  if (raw === '') {
                    handleChange({ manual_total_amount: null });
                    return;
                  }
                  const manualAmount = Number(raw);
                  // ★ ЗАЩИТА: максимум 99,999,999 ₽
                  if (manualAmount > 99_999_999) {
                    toast({
                      title: "Ошибка",
                      description: "Максимальная сумма: 99,999,999 ₽",
                      variant: "destructive",
                    });
                    return;
                  }
                  handleChange({
                    manual_total_amount: Number.isFinite(manualAmount) ? manualAmount : null,
                    total_amount: Number.isFinite(manualAmount) ? manualAmount : formData.total_amount
                  });
                }}
                min="0"
                max={99_999_999}
                step="100"
                className="mt-1"
                placeholder={String(formData.total_amount || 0)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Укажите фиксированную сумму вручную
              </p>
            </div>
          )}

          <div className="flex items-center space-x-2 p-3 border rounded-lg bg-background" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={formData.price_locked}
              onCheckedChange={(checked) => {
                // При включении — фиксируем текущую сумму (иначе "не сохраняется"/не применяется)
                if (checked) {
                  handleChange({
                    price_locked: true,
                    manual_total_amount: formData.manual_total_amount ?? formData.total_amount
                  });
                } else {
                  handleChange({ price_locked: false });
                }
              }}
            />
            <div className="flex-1">
              <Label className="flex items-center gap-2 cursor-pointer">
                {formData.price_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                Зафиксировать стоимость (не пересчитывать при изменениях)
              </Label>
            </div>
          </div>
          
          {!formData.price_locked && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Цена пересчитывается автоматически при изменении дат, номера или тарифа
            </p>
          )}
        </CardContent>
      </Card>

      {/* 📊 Управление статусом */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-primary" />
            Управление статусом
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Текущий статус</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {getStatusLabel(booking.status)}
              </p>
            </div>
            <Badge className={cn(
              "text-sm px-3 py-1",
              booking.status === 'confirmed' && "bg-green-500",
              booking.status === 'pending' && "bg-yellow-500",
              booking.status === 'checked_in' && "bg-blue-500",
              booking.status === 'checked_out' && "bg-gray-500",
              booking.status === 'cancelled' && "bg-red-500",
            )}>
              {getStatusLabel(booking.status)}
            </Badge>
          </div>

          {booking.status !== 'cancelled' ? (
            <div className="space-y-3">
              <Select
                value={booking.status}
                onValueChange={(value) => handleStatusChange(value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      Подтверждено
                    </span>
                  </SelectItem>
                  <SelectItem value="checked_in">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      Заселен
                    </span>
                  </SelectItem>
                  <SelectItem value="checked_out">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-gray-500" />
                      Выселен
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>

              {booking.status !== 'checked_out' && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Аннулировать
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Причина отмены */}
              {(booking as any).cancellation_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    <span className="font-medium">Причина:</span> {(booking as any).cancellation_reason}
                  </p>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={openRestoreDialog}
                className="w-full bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Восстановить бронь
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="w-full bg-muted hover:bg-muted/80 text-muted-foreground border-muted-foreground/30"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                В корзину
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 👑 VIP Статус (фишка Риксос) */}
      {formData.vip_status && (
        <Card className="border-primary bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Crown className="h-8 w-8 text-primary" />
              <div>
                <h3 className="text-lg font-bold">VIP Гость</h3>
                <p className="text-sm text-muted-foreground">
                  Премиум обслуживание • Комплимент при заезде • Трансфер • Приоритетная поддержка
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 🌟 Дополнительные услуги Риксос */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Дополнительные услуги
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-primary" />
              <div>
                <Label className="font-medium">VIP-статус</Label>
                <p className="text-xs text-muted-foreground">Премиум обслуживание и привилегии</p>
              </div>
            </div>
            <Switch
              checked={formData.vip_status}
              onCheckedChange={(checked) => handleChange({ vip_status: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-3 border rounded-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <Car className="w-5 h-5 text-primary" />
              <div>
                <Label className="font-medium">Трансфер</Label>
                <p className="text-xs text-muted-foreground">Встреча в аэропорту</p>
              </div>
            </div>
            <Switch
              checked={formData.transfer_needed}
              onCheckedChange={(checked) => handleChange({ transfer_needed: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-3 border rounded-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-orange-500" />
              <div>
                <Label className="font-medium">Блокировка переселения</Label>
                <p className="text-xs text-muted-foreground">Запретить перемещение в другой номер</p>
              </div>
            </div>
            <Switch
              checked={formData.no_relocation}
              onCheckedChange={(checked) => handleChange({ no_relocation: checked })}
            />
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <UtensilsCrossed className="w-4 h-4" />
              Диетические ограничения
            </Label>
            <Input
              value={formData.dietary_restrictions}
              onChange={(e) => handleChange({ dietary_restrictions: e.target.value })}
              placeholder="Вегетарианство, аллергии и т.д."
              className="mt-1"
            />
          </div>

          {/* 🏨 История/связанные бронирования гостя (по guest_id) */}
          <div className="pt-3">
            <GuestLinkedRooms booking={booking} onBookingSelect={onBookingSelect} />
          </div>
        </CardContent>
      </Card>

      {/* 💾 Кнопка сохранения - Мировой уровень */}
      <div className="sticky bottom-4 z-10">
        <Button 
          onClick={() => handleSave()}
          disabled={isValidating || isSaving || isSaved}
          className={cn(
            "relative w-full h-12 text-base font-semibold rounded-xl overflow-hidden",
            "border-0 transition-all duration-300 ease-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "transform-gpu will-change-transform hover:!scale-100 active:!scale-100",
            "disabled:cursor-not-allowed",
            "before:absolute before:inset-0 before:opacity-0 before:transition-opacity before:duration-300",
            isSaved
              ? "bg-gradient-to-r from-green-500 via-green-600 to-green-500 shadow-[0_10px_28px_-10px_rgba(34,197,94,0.45)] hover:shadow-[0_14px_40px_-12px_rgba(34,197,94,0.55)] disabled:opacity-90"
              : "bg-gradient-to-r from-primary via-primary to-primary/90 shadow-[0_10px_28px_-10px_hsl(var(--primary)/0.45)] hover:shadow-[0_14px_40px_-12px_hsl(var(--primary)/0.55)] disabled:opacity-70",
            !isSaved && "before:bg-[radial-gradient(1200px_circle_at_20%_0%,hsl(var(--primary-foreground)/0.20),transparent_55%)] hover:before:opacity-100"
          )}
          size="lg"
        >
          {isSaving || isValidating ? (
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Сохранение...</span>
            </div>
          ) : isSaved ? (
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-lg bg-white/20">
                <Check className="h-5 w-5" />
              </div>
              <span>Сохранено</span>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-lg bg-white/20">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <span>Сохранить изменения</span>
            </div>
          )}
        </Button>
      </div>

      {/* 🚨 Диалог подтверждения аннулирования с причиной */}
      <AlertDialog open={showCancelDialog} onOpenChange={(open) => {
        setShowCancelDialog(open);
        if (!open) { setCancellationReason(''); setCancellationComment(''); }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Аннулировать бронирование?</AlertDialogTitle>
            <AlertDialogDescription>
              Укажите причину отмены. Бронирование можно будет восстановить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Причина отмены *</Label>
              <Select value={cancellationReason} onValueChange={setCancellationReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите причину..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Гость отменил">Гость отменил</SelectItem>
                  <SelectItem value="Нет оплаты">Нет оплаты</SelectItem>
                  <SelectItem value="Овербукинг">Овербукинг</SelectItem>
                  <SelectItem value="Дублирование брони">Дублирование брони</SelectItem>
                  <SelectItem value="Изменение дат">Изменение дат (перебронирование)</SelectItem>
                  <SelectItem value="No-show">No-show (неявка)</SelectItem>
                  <SelectItem value="Форс-мажор">Форс-мажор</SelectItem>
                  <SelectItem value="Другое">Другое</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {cancellationReason === 'Другое' ? 'Укажите причину *' : 'Комментарий (необязательно)'}
              </Label>
              <Textarea
                value={cancellationComment}
                onChange={(e) => setCancellationComment(e.target.value)}
                placeholder="Дополнительная информация..."
                className="min-h-[60px]"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelBooking}
              disabled={isCancelling || !cancellationReason || (cancellationReason === 'Другое' && !cancellationComment.trim())}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling ? 'Аннулирование...' : 'Да, аннулировать'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 🔄 Диалог восстановления брони */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-600" />
              Восстановить бронирование
            </DialogTitle>
            <DialogDescription>
              Выберите категорию и номер для восстановления брони
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Категория номера</Label>
              <Select value={restoreCategoryId} onValueChange={(val) => { setRestoreCategoryId(val); setRestoreRoomId(''); fetchRestoreConflicts(val); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите категорию..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Номер</Label>
              <Select value={restoreRoomId} onValueChange={setRestoreRoomId}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите номер..." />
                </SelectTrigger>
                <SelectContent>
                  {restoreAvailableRooms.map(room => {
                    const conflicts = restoreConflicts[room.id];
                    const isOccupied = conflicts && conflicts.length > 0;
                    const conflictLabel = isOccupied 
                      ? `занят (${conflicts.map(c => `${format(new Date(c.check_in_date), 'dd.MM')}-${format(new Date(c.check_out_date), 'dd.MM')}`).join(', ')})` 
                      : 'свободен';
                    return (
                      <SelectItem key={room.id} value={room.id} disabled={isOccupied}>
                        {room.room_number} — {conflictLabel}
                      </SelectItem>
                    );
                  })}
                  {restoreAvailableRooms.length === 0 && restoreCategoryId && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Нет номеров в категории</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>Отмена</Button>
            <Button
              onClick={handleRestoreBooking}
              disabled={isRestoring || !restoreRoomId}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isRestoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Восстановить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🗑️ Диалог подтверждения удаления */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-muted-foreground" />
              Удалить бронирование?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Бронирование будет перемещено в корзину. Вы сможете восстановить его 
              в течение 30 дней на странице "Удалённые бронирования".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSoftDelete}
              disabled={isDeleting}
              className="bg-muted text-muted-foreground hover:bg-muted/80"
            >
              {isDeleting ? 'Удаление...' : 'Да, удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 🎯 Этап 2.2: Диалог выбора номера при смене категории */}
      <AlertDialog open={roomTypeChangeDialog.show} onOpenChange={(open) => {
        if (!open) setRoomTypeChangeDialog({ show: false });
      }}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <DoorOpen className="w-5 h-5 text-primary" />
              Выберите номер в категории "{roomTypeChangeDialog.newRoomType}"
            </AlertDialogTitle>
            <AlertDialogDescription>
              {availabilityInvalidRange
                ? 'Укажите корректные даты — выезд должен быть позже заезда'
                : isCheckingAvailability
                  ? 'Проверяем доступность на сервере…'
                  : availabilityFailed
                    ? 'Проверка недоступна — сервер не ответил'
                    : `Свободно ${dialogRooms.filter(r => isRoomAvailable(r)).length} из ${dialogRooms.length} номеров на выбранные даты (${format(new Date(formData.check_in_date), 'dd.MM.yy')} - ${format(new Date(formData.check_out_date), 'dd.MM.yy')})`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="grid gap-3 max-h-[400px] overflow-y-auto py-4">
            {dialogRooms.length === 0 && (
              <p className="text-sm text-muted-foreground">В этой категории нет номеров</p>
            )}
            {dialogRooms.map((room) => {
              const row = availabilityByRoom[room.id];
              const pending = isCheckingAvailability || availabilityInvalidRange || (!row && !availabilityFailed);
              const free = Boolean(row?.is_available);
              const clickable = free && !pending;
              return (
                <div
                  key={room.id}
                  className={cn(
                    "flex items-center justify-between p-4 border rounded-lg transition-colors",
                    clickable ? "hover:bg-muted/50 cursor-pointer" : "opacity-60 cursor-not-allowed"
                  )}
                  onClick={() => { if (clickable) applyRoomChange(room); }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                      <DoorOpen className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Номер {room.room_number}</h4>
                      <p className="text-sm text-muted-foreground">
                        {room.name || room.room_type}
                        {room.floor && ` • ${room.floor} этаж`}
                        {room.view_type && ` • ${room.view_type}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={cn("font-semibold", free && !pending ? "text-emerald-600" : "text-muted-foreground")}>
                      {pending
                        ? 'Проверяем…'
                        : availabilityFailed
                          ? 'Проверка недоступна'
                          : free
                            ? 'Свободен'
                            : 'Занят'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {!pending && !free && row ? conflictText(row) : 'Цена рассчитывается из тарифов'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 🚨 Диалог подтверждения раннего заселения */}
      <AlertDialog open={showEarlyCheckinDialog} onOpenChange={setShowEarlyCheckinDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className={cn(
                "w-5 h-5",
                earlyCheckinDays > 30 ? "text-destructive" : "text-amber-500"
              )} />
              {earlyCheckinDays > 30 ? '⚠️ Экстремально ранний заезд!' : '⚠️ Ранний заезд'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className={cn(
                  "font-semibold text-lg",
                  earlyCheckinDays > 30 ? "text-destructive" : "text-amber-600"
                )}>
                  До даты заезда ещё {earlyCheckinDays} {earlyCheckinDays === 1 ? 'день' : earlyCheckinDays < 5 ? 'дня' : 'дней'}.
                </p>
                <div className="bg-muted p-3 rounded-lg space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Дата заезда:</span>
                    <span className="font-medium">{format(new Date(booking.check_in_date), 'd MMMM yyyy', { locale: ru })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Сегодня:</span>
                    <span className="font-medium">{format(new Date(), 'd MMMM yyyy', { locale: ru })}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Вы уверены, что хотите заселить гостя?
                </p>
                {earlyCheckinDays > 30 && (
                  <div className="bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
                    <p className="text-destructive text-sm font-medium">
                      ⚠️ Это очень ранняя дата заезда. Возможно, это ошибка.
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowEarlyCheckinDialog(false)}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowEarlyCheckinDialog(false);
                handleStatusChange('checked_in', true); // forceEarlyCheckin = true
              }}
              className={cn(
                earlyCheckinDays > 30 ? "bg-destructive hover:bg-destructive/90" : "bg-amber-600 hover:bg-amber-700"
              )}
            >
              Заселить всё равно
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};